import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { FiArrowLeft, FiEdit, FiSave, FiX, FiUsers, FiClock, FiPhone, FiMail, FiMapPin, FiMap, FiUpload, FiTrash2, FiSearch, FiCamera, FiImage, FiDownload, FiPrinter, FiFolder, FiFile, FiChevronRight, FiDatabase, FiLink, FiCheckSquare, FiPlus, FiChevronDown, FiShare2, FiSend, FiTruck, FiGlobe, FiCopy, FiLayers, FiRefreshCw } from 'react-icons/fi';
import { getJob, updateJob, getAuthToken, getParcelFolders, getParcelProjects, previewParcelData, importParcelsToJob, getJobParcels, deleteJobParcel, clearJobParcels, uploadParcelsJson, getChangeOrders, createChangeOrder, updateChangeOrder, deleteChangeOrder, updateContractAmount } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import SelfAssignButton from '../components/SelfAssignButton';
import JobComments from '../components/JobComments';
import WorkOrderPrint from '../components/WorkOrderPrint';
import FieldChecklistModal from '../components/FieldChecklistModal';
import RelatedLetters from '../components/RelatedLetters';
import JobAttachments from '../components/JobAttachments';
import EmailToClientModal from '../components/EmailToClientModal';
import JobOverview from '../components/JobOverview';
import JobTimer from '../components/JobTimer';
import { API_URL, API_BASE_URL } from '../config';
import { appendDeviceLocation } from '../utils/geotag';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to handle map clicks
function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Component to recenter map
function MapRecenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 15);
    }
  }, [center, map]);
  return null;
}

// Helper to get axios config with auth token
const getAuthConfig = async () => {
  const token = await getAuthToken();
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

// Cancel-job modal — captures a required reason + optional notes, then POSTs to /cancel
function CancelJobModal({ job, onClose, onCancelled }) {
  const REASONS = [
    'Went with another firm',
    'Price too high',
    'Timeline doesn\'t work',
    'Project cancelled entirely',
    'No response from client',
    'Other',
  ];
  const [reason, setReason] = useState(REASONS[0]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason) return;
    setSubmitting(true);
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/jobs/${job.id}/cancel`, { reason, notes }, config);
      onCancelled();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to cancel job');
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '16px',
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '24px',
        width: '100%', maxWidth: '480px', display: 'flex', flexDirection: 'column', gap: '14px',
        maxHeight: 'calc(90dvh - 90px)', overflowY: 'auto',
      }}>
        <h2 style={{ margin: 0, fontSize: '18px', color: '#991b1b' }}>Cancel Job {job.job_number}?</h2>
        <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', lineHeight: 1.5 }}>
          This marks the job as Cancelled (preserves the record for reporting), pulls it off the schedule, deactivates the client portal, and marks the linked quote as Cancelled. If the client comes back, you can Reactivate it later to put it back on the schedule.
        </p>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>Reason *</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}>
            {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>Notes <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Anything specific worth remembering?" style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '6px' }}>
          <button onClick={onClose} disabled={submitting} style={{ padding: '10px 18px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
            Keep Job
          </button>
          <button onClick={submit} disabled={submitting} style={{
            padding: '10px 18px', backgroundColor: submitting ? '#9ca3af' : '#dc2626', color: 'white',
            border: 'none', borderRadius: '8px', cursor: submitting ? 'wait' : 'pointer',
            fontSize: '14px', fontWeight: 600,
          }}>
            {submitting ? 'Cancelling…' : 'Cancel Job'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Reactivate-job modal — picks a schedule date, then POSTs to /reactivate
function ReactivateJobModal({ job, onClose, onReactivated }) {
  const [schedDate, setSchedDate] = useState('');
  const [schedTime, setSchedTime] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!schedDate) return;
    setSubmitting(true);
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/jobs/${job.id}/reactivate`,
        { scheduled_date: schedDate, scheduled_time: schedTime || undefined }, config);
      onReactivated();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to reactivate job');
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '16px',
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '24px',
        width: '100%', maxWidth: '440px', display: 'flex', flexDirection: 'column', gap: '14px',
      }}>
        <h2 style={{ margin: 0, fontSize: '18px', color: '#065f46' }}>Reactivate Job {job.job_number}</h2>
        <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', lineHeight: 1.5 }}>
          Pick the date to put this job back on the schedule. This also clears the cancellation,
          turns the client portal back on, and restores the approved quote.
        </p>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>Schedule date *</label>
          <input type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)}
            style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>Time <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input type="time" value={schedTime} onChange={(e) => setSchedTime(e.target.value)}
            style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '6px' }}>
          <button onClick={onClose} disabled={submitting} style={{ padding: '10px 18px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
            Cancel
          </button>
          <button onClick={submit} disabled={submitting || !schedDate} style={{
            padding: '10px 18px', backgroundColor: (submitting || !schedDate) ? '#9ca3af' : '#059669', color: 'white',
            border: 'none', borderRadius: '8px', cursor: (submitting || !schedDate) ? 'not-allowed' : 'pointer',
            fontSize: '14px', fontWeight: 600,
          }}>
            {submitting ? 'Reactivating…' : 'Reactivate & Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper to parse UTC timestamps from backend
const parseUTCDate = (dateStr) => {
  if (!dateStr) return null;
  let timestamp = dateStr;
  if (!timestamp.endsWith('Z') && !timestamp.includes('+')) {
    timestamp = timestamp + 'Z';
  }
  return new Date(timestamp);
};

function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Where did the user come from? Set via Link state={{from, label}} on the calling page.
  // Falls back to sessionStorage so a page refresh keeps the correct back target,
  // and finally defaults to /jobs if we have no idea.
  const stateFrom = location.state?.from;
  const stateLabel = location.state?.label;
  const storageKey = `jobBack_${id}`;
  if (stateFrom && stateLabel) {
    try { sessionStorage.setItem(storageKey, JSON.stringify({ from: stateFrom, label: stateLabel })); } catch (e) {}
  }
  let savedBack = null;
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (raw) savedBack = JSON.parse(raw);
  } catch (e) {}
  const backPath = stateFrom || savedBack?.from || '/jobs';
  const backLabel = stateLabel || savedBack?.label || 'Jobs';
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [jobTimeEntries, setJobTimeEntries] = useState([]);
  const [totalJobHours, setTotalJobHours] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [uploadingBoundary, setUploadingBoundary] = useState(false);
  
  // Photo state
  const [photos, setPhotos] = useState([]);
  const [showPhotoEmail, setShowPhotoEmail] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [uploadingParcel, setUploadingParcel] = useState(false);
  
  // Work Order Print state
  const [showWorkOrder, setShowWorkOrder] = useState(false);
  const [showTimeTracking, setShowTimeTracking] = useState(false);
  
  // Map picker state
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [mapCenter, setMapCenter] = useState([34.0522, -84.5]);
  const [searchAddress, setSearchAddress] = useState('');
  const [searching, setSearching] = useState(false);
  const [tempCoords, setTempCoords] = useState({ lat: null, lng: null });
  const [mapView, setMapView] = useState('aerial');

  // Profitability state (admin only)
  const [profitability, setProfitability] = useState(null);
  const [expenses, setExpenses] = useState({
    rebar_count: 0,
    pipe_count: 0,
    num_trips: 1,
    crew_size: 1,
    other_expenses: 0,
    other_expenses_note: ''
  });
  const [savingExpenses, setSavingExpenses] = useState(false);
  const [showProfitability, setShowProfitability] = useState(false);
  const canManagePayroll = user?.can_manage_payroll || false;

  // Work Orders state
  const [workOrders, setWorkOrders] = useState([]);
  const [workOrdersSummary, setWorkOrdersSummary] = useState({});
  const [showWorkOrders, setShowWorkOrders] = useState(false);
  const [showWorkOrderForm, setShowWorkOrderForm] = useState(false);
  const [editingWorkOrder, setEditingWorkOrder] = useState(null);
  const [workOrderForm, setWorkOrderForm] = useState({
    description: '',
    po_number: '',
    quoted_amount: '',
    status: 'Pending',
    scheduled_date: '',
    notes: ''
  });

  // Change Order state
  const [changeOrders, setChangeOrders] = useState([]);
  const [showChangeOrders, setShowChangeOrders] = useState(false);
  const [showCOForm, setShowCOForm] = useState(false);
  const [editingCO, setEditingCO] = useState(null);
  const [contractAmount, setContractAmount] = useState('');
  const [editingContract, setEditingContract] = useState(false);
  const [coForm, setCOForm] = useState({ title: '', description: '', reason: '', amount: '', requested_by: '' });
  const [coSummary, setCOSummary] = useState({ total_change_orders: 0, total_contract_value: 0 });

  // Parcel Import state
  const [jobParcels, setJobParcels] = useState([]);
  const [showParcelImport, setShowParcelImport] = useState(false);
  const [parcelFolders, setParcelFolders] = useState([]);
  const [parcelProjects, setParcelProjects] = useState([]);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [previewParcels, setPreviewParcels] = useState([]);
  const [importingParcels, setImportingParcels] = useState(false);

  // Field checklists
  const [fieldChecklists, setFieldChecklists] = useState([]);
  const [showFieldChecklists, setShowFieldChecklists] = useState(false);
  const [customChecklistItems, setCustomChecklistItems] = useState([]);
  const [newCustomItem, setNewCustomItem] = useState('');
  const [editingFieldChecklist, setEditingFieldChecklist] = useState(null); // { id, job_id, job_number }

  // Job Contacts
  const [jobContacts, setJobContacts] = useState([]);
  const [showContacts, setShowContacts] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', phone: '', email: '', role: '' });
  const [editingContact, setEditingContact] = useState(null); // contact id being edited
  const [editContactForm, setEditContactForm] = useState({ name: '', phone: '', email: '', role: '' });
  // "Add from company" picker — the client's saved roster (foreman, AP, PM)
  const [showRosterPicker, setShowRosterPicker] = useState(false);
  const [roster, setRoster] = useState({ client_label: '', candidates: [] });
  const [rosterPicked, setRosterPicked] = useState([]); // client_contact ids ticked
  const [rosterBusy, setRosterBusy] = useState(false);

  // Client Portal
  const [portalData, setPortalData] = useState(null);
  const [portalNotifications, setPortalNotifications] = useState([]);
  const [showPortal, setShowPortal] = useState(false);
  const [portalMessage, setPortalMessage] = useState({ title: '', message: '' });
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifySelection, setNotifySelection] = useState([]); // indices of selected contacts
  const [notifyMessage, setNotifyMessage] = useState({ title: '', message: '', send_sms: true, send_email: true });
  const [loadingParcels, setLoadingParcels] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const isCapacitor = window.Capacitor?.isNativePlatform?.() || false;
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isCapacitor || isSmallScreen);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    loadJob();
    loadJobTimeEntries();
    loadPhotos();
    loadJobParcels();
    loadFieldChecklists();
    loadCustomChecklistItems();
    loadJobContacts();
    loadPortal();
  }, [id]);

  useEffect(() => {
    if (id) {
      loadWorkOrders();  // All users can see work orders
      loadChangeOrders();
      if (canManagePayroll) {
        loadProfitability();  // Only payroll managers see profitability
      }
    }
  }, [id, canManagePayroll]);

  const loadJob = async () => {
    try {
      const response = await getJob(id);
      setJob(response.data);
      setFormData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading job:', error);
      setLoading(false);
    }
  };

  const loadJobTimeEntries = async () => {
    try {
      // Get job time entries (visible to all users)
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/time-entries?type=job`, config);
      // Filter entries for this job
      const entries = response.data.time_entries || response.data || [];
      const jobEntries = entries.filter(
        entry => entry.job_id === parseInt(id)
      );
      setJobTimeEntries(jobEntries);
      
      // Calculate total hours
      const total = jobEntries.reduce((sum, entry) => sum + (entry.total_hours || 0), 0);
      setTotalJobHours(total);
    } catch (error) {
      console.error('Error loading time entries:', error);
    }
  };

  const loadPhotos = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/jobs/${id}/photos`, config);
      setPhotos(response.data.photos || []);
    } catch (error) {
      console.error('Error loading photos:', error);
    }
  };

  // Parcel Import Functions
  const loadJobParcels = async () => {
    try {
      const response = await getJobParcels(id);
      setJobParcels(response.data.parcels || []);
    } catch (error) {
      console.error('Error loading job parcels:', error);
    }
  };

  const openParcelImport = async () => {
    setShowParcelImport(true);
    setLoadingParcels(true);
    setSelectedCounty(null);
    setSelectedProject(null);
    setPreviewParcels([]);
    try {
      const response = await getParcelFolders();
      setParcelFolders(response.data.folders || []);
    } catch (error) {
      console.error('Error loading parcel folders:', error);
      setParcelFolders([]);
    } finally {
      setLoadingParcels(false);
    }
  };

  const handleJsonUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      alert('Please select a JSON file.');
      return;
    }
    setImportingParcels(true);
    try {
      const response = await uploadParcelsJson(id, file);
      alert(`Successfully imported ${response.data.imported_count} parcels from JSON!`);
      setShowParcelImport(false);
      loadJobParcels();
    } catch (error) {
      console.error('Error uploading JSON:', error);
      alert('Failed to import: ' + (error.response?.data?.error || error.message));
    } finally {
      setImportingParcels(false);
      e.target.value = '';
    }
  };

  const selectCounty = async (county) => {
    setSelectedCounty(county);
    setSelectedProject(null);
    setPreviewParcels([]);
    setLoadingParcels(true);
    try {
      const response = await getParcelProjects(county.path);
      setParcelProjects(response.data.projects || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoadingParcels(false);
    }
  };

  const selectProject = async (project) => {
    setSelectedProject(project);
    setLoadingParcels(true);
    try {
      const response = await previewParcelData(project.path);
      setPreviewParcels(response.data.parcels || []);
    } catch (error) {
      console.error('Error previewing parcel data:', error);
      alert('Could not load parcel data from this project.');
    } finally {
      setLoadingParcels(false);
    }
  };

  const handleImportParcels = async () => {
    if (!selectedProject) return;
    setImportingParcels(true);
    try {
      const response = await importParcelsToJob(id, selectedProject.path);
      alert(`Successfully imported ${response.data.imported_count} parcels!`);
      setShowParcelImport(false);
      loadJobParcels();
    } catch (error) {
      console.error('Error importing parcels:', error);
      alert('Failed to import parcels: ' + (error.response?.data?.error || error.message));
    } finally {
      setImportingParcels(false);
    }
  };

  const handleDeleteParcel = async (parcelId) => {
    if (!window.confirm('Delete this parcel from the job?')) return;
    try {
      await deleteJobParcel(id, parcelId);
      loadJobParcels();
    } catch (error) {
      console.error('Error deleting parcel:', error);
      alert('Failed to delete parcel');
    }
  };

  const handleClearAllParcels = async () => {
    if (!window.confirm('Remove ALL imported parcels from this job? This cannot be undone.')) return;
    try {
      await clearJobParcels(id);
      loadJobParcels();
    } catch (error) {
      console.error('Error clearing parcels:', error);
      alert('Failed to clear parcels');
    }
  };

  const loadProfitability = async () => {
    if (!canManagePayroll) return;
    try {
      const config = await getAuthConfig();
      const [profitRes, expenseRes] = await Promise.all([
        axios.get(`${API_URL}/jobs/${id}/profitability`, config),
        axios.get(`${API_URL}/jobs/${id}/expenses`, config)
      ]);
      setProfitability(profitRes.data);
      setExpenses(expenseRes.data);
    } catch (error) {
      console.error('Error loading profitability:', error);
    }
  };

  const saveExpenses = async () => {
    setSavingExpenses(true);
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/jobs/${id}/expenses`, expenses, config);
      await loadProfitability();
    } catch (error) {
      console.error('Error saving expenses:', error);
      alert('Failed to save expenses');
    }
    setSavingExpenses(false);
  };

  // Change Order Functions
  const loadChangeOrders = async () => {
    try {
      const res = await getChangeOrders(id);
      setChangeOrders(res.data.change_orders || []);
      setCOSummary({ total_change_orders: res.data.total_change_orders, total_contract_value: res.data.total_contract_value });
      setContractAmount(res.data.contract_amount || '');
    } catch (error) {
      console.error('Error loading change orders:', error);
    }
  };

  const handleSaveContract = async () => {
    try {
      await updateContractAmount(id, parseFloat(contractAmount) || 0);
      setEditingContract(false);
      loadChangeOrders();
    } catch (err) {
      alert('Failed to save contract amount');
    }
  };

  const handleCOSubmit = async () => {
    try {
      const payload = { ...coForm, amount: parseFloat(coForm.amount) || 0 };
      if (editingCO) {
        await updateChangeOrder(id, editingCO.id, payload);
      } else {
        await createChangeOrder(id, payload);
      }
      setShowCOForm(false);
      setEditingCO(null);
      setCOForm({ title: '', description: '', reason: '', amount: '', requested_by: '' });
      loadChangeOrders();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save change order');
    }
  };

  const handleDeleteCO = async (coId) => {
    if (!window.confirm('Delete this change order?')) return;
    try {
      await deleteChangeOrder(id, coId);
      loadChangeOrders();
    } catch (err) {
      alert('Failed to delete change order');
    }
  };

  const handleCOStatusChange = async (co, newStatus) => {
    try {
      await updateChangeOrder(id, co.id, { status: newStatus });
      loadChangeOrders();
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const openCOPdf = async (coId) => {
    const config = await getAuthConfig();
    const token = config.headers?.Authorization?.replace('Bearer ', '') || '';
    window.open(`${API_URL}/jobs/${id}/change-orders/${coId}/pdf?token=${token}`, '_blank');
  };

  const handleEmailCO = async (co) => {
    const defaultEmail = job?.billing_email || job?.client_email || '';
    const to = window.prompt('Email the change order approval link to:', defaultEmail);
    if (!to) return;
    const message = window.prompt('Optional message to include (leave blank to skip):', '') || '';
    try {
      const config = await getAuthConfig();
      const res = await axios.post(
        `${API_URL}/jobs/${id}/change-orders/${co.id}/email`,
        { to_email: to.trim(), message },
        config
      );
      alert(res.data?.message || `Change order emailed to ${to}`);
      loadChangeOrders();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to email change order');
    }
  };

  // Work Order Functions
  const loadWorkOrders = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/jobs/${id}/work-orders`, config);
      setWorkOrders(response.data.work_orders || []);
      setWorkOrdersSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error loading work orders:', error);
    }
  };

  const loadFieldChecklists = async () => {
    try {
      const config = await getAuthConfig();
      const res = await axios.get(`${API_URL}/jobs/${id}/field-checklists`, config);
      setFieldChecklists(res.data || []);
    } catch (error) {
      console.error('Error loading field checklists:', error);
    }
  };

  const loadCustomChecklistItems = async () => {
    try {
      const config = await getAuthConfig();
      const res = await axios.get(`${API_URL}/jobs/${id}/custom-checklist-items`, config);
      setCustomChecklistItems(res.data || []);
    } catch (error) {
      console.error('Error loading custom checklist items:', error);
    }
  };

  const handleAddCustomItem = async () => {
    if (!newCustomItem.trim()) return;
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/jobs/${id}/custom-checklist-items`, { item_text: newCustomItem.trim() }, config);
      setNewCustomItem('');
      loadCustomChecklistItems();
    } catch (error) {
      alert('Failed to add custom item');
    }
  };

  const handleDeleteCustomItem = async (itemId) => {
    try {
      const config = await getAuthConfig();
      await axios.delete(`${API_URL}/jobs/${id}/custom-checklist-items/${itemId}`, config);
      loadCustomChecklistItems();
    } catch (error) {
      alert('Failed to delete custom item');
    }
  };

  const loadJobContacts = async () => {
    try {
      const config = await getAuthConfig();
      const res = await axios.get(`${API_URL}/jobs/${id}/contacts`, config);
      setJobContacts(res.data || []);
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  const handleAddContact = async () => {
    if (!newContact.name.trim()) { alert('Name is required'); return; }
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/jobs/${id}/contacts`, newContact, config);
      setNewContact({ name: '', phone: '', email: '', role: '' });
      loadJobContacts();
    } catch (error) {
      alert('Failed to add contact');
    }
  };

  const handleSaveContact = async () => {
    if (!editingContact || !editContactForm.name.trim()) return;
    try {
      const config = await getAuthConfig();
      await axios.put(`${API_URL}/jobs/${id}/contacts/${editingContact}`, editContactForm, config);
      setEditingContact(null);
      loadJobContacts();
    } catch (error) {
      alert('Failed to update contact');
    }
  };

  // Open the picker and pull the client company's roster. Pre-ticks nobody — adding a
  // contact starts sending them job notifications, so it should be a deliberate choice.
  const openRosterPicker = async () => {
    setShowRosterPicker(true);
    setRosterBusy(true);
    setRosterPicked([]);
    try {
      const config = await getAuthConfig();
      const res = await axios.get(`${API_URL}/jobs/${id}/roster-candidates`, config);
      setRoster(res.data || { client_label: '', candidates: [] });
    } catch (error) {
      console.error('Error loading company contacts:', error);
      setRoster({ client_label: '', candidates: [] });
    } finally {
      setRosterBusy(false);
    }
  };

  const toggleRosterPick = (contactId) => {
    setRosterPicked(prev => prev.includes(contactId)
      ? prev.filter(x => x !== contactId)
      : [...prev, contactId]);
  };

  const handleAddFromRoster = async () => {
    if (rosterPicked.length === 0) return;
    setRosterBusy(true);
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/jobs/${id}/contacts/from-roster`,
        { client_contact_ids: rosterPicked }, config);
      setShowRosterPicker(false);
      setRosterPicked([]);
      loadJobContacts();
    } catch (error) {
      alert('Failed to add contacts from the company list');
    } finally {
      setRosterBusy(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Remove this contact?')) return;
    try {
      const config = await getAuthConfig();
      await axios.delete(`${API_URL}/jobs/${id}/contacts/${contactId}`, config);
      loadJobContacts();
    } catch (error) {
      alert('Failed to delete contact');
    }
  };

  const loadPortal = async () => {
    try {
      const config = await getAuthConfig();
      const res = await axios.get(`${API_URL}/jobs/${id}/portal`, config);
      setPortalData(res.data);
      const notifRes = await axios.get(`${API_URL}/jobs/${id}/portal/notifications`, config);
      setPortalNotifications(notifRes.data || []);
    } catch (error) {
      console.error('Error loading portal:', error);
    }
  };

  const handleSendPortalMessage = async () => {
    if (!portalMessage.title.trim()) { alert('Title is required'); return; }
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/jobs/${id}/portal/message`, portalMessage, config);
      setPortalMessage({ title: '', message: '' });
      loadPortal();
    } catch (error) {
      alert('Failed to send message');
    }
  };

  const handleEnRoute = async () => {
    if (!window.confirm('Notify the client that you are headed to the site?')) return;
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/jobs/${id}/portal/en-route`, {}, config);
      alert('Client has been notified!');
      loadPortal();
    } catch (error) {
      alert('Failed to notify client');
    }
  };

  const handleTogglePortal = async () => {
    try {
      const config = await getAuthConfig();
      const res = await axios.post(`${API_URL}/jobs/${id}/portal/toggle`, {}, config);
      setPortalData(res.data);
    } catch (error) {
      alert('Failed to toggle portal');
    }
  };

  const handleWorkOrderSubmit = async () => {
    try {
      const config = await getAuthConfig();
      if (editingWorkOrder) {
        await axios.put(`${API_URL}/jobs/${id}/work-orders/${editingWorkOrder.id}`, workOrderForm, config);
      } else {
        await axios.post(`${API_URL}/jobs/${id}/work-orders`, workOrderForm, config);
      }
      setShowWorkOrderForm(false);
      setEditingWorkOrder(null);
      setWorkOrderForm({
        description: '',
        po_number: '',
        quoted_amount: '',
        status: 'Pending',
        scheduled_date: '',
        notes: ''
      });
      loadWorkOrders();
      loadJob(); // Refresh job to update is_multi_phase
    } catch (error) {
      console.error('Error saving work order:', error);
      alert('Failed to save work order');
    }
  };

  const handleEditWorkOrder = (wo) => {
    setEditingWorkOrder(wo);
    setWorkOrderForm({
      description: wo.description || '',
      po_number: wo.po_number || '',
      quoted_amount: wo.quoted_amount || '',
      status: wo.status || 'Pending',
      scheduled_date: wo.scheduled_date || '',
      completed_date: wo.completed_date || '',
      notes: wo.notes || ''
    });
    setShowWorkOrderForm(true);
  };

  const handleDeleteWorkOrder = async (woId) => {
    if (!window.confirm('Delete this work order?')) return;
    try {
      const config = await getAuthConfig();
      await axios.delete(`${API_URL}/jobs/${id}/work-orders/${woId}`, config);
      loadWorkOrders();
      loadJob();
    } catch (error) {
      console.error('Error deleting work order:', error);
      alert('Failed to delete work order');
    }
  };

  const getWorkOrderStatusColor = (status) => {
    const colors = {
      'Pending': '#f59e0b',
      'Scheduled': '#3b82f6',
      'In Progress': '#8b5cf6',
      'Completed': '#10b981',
      'Invoiced': '#06b6d4',
      'Paid': '#16a34a'
    };
    return colors[status] || '#6b7280';
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingPhoto(true);
    try {
      const config = await getAuthConfig();
      const formData = new FormData();
      formData.append('photo', file);
      await appendDeviceLocation(formData, file); // geotag fresh captures for corner-photo map

      const res = await axios.post(`${API_URL}/jobs/${id}/photos`, formData, {
        ...config,
        headers: {
          ...config.headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      // No signal — held in the offline outbox and uploaded automatically later.
      if (res?.data?.__queued) {
        alert('No signal — photo saved on this device. It will upload automatically when you have service.');
      }
      loadPhotos();
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert(error?.__offlineStorageFull ? error.message : 'Failed to upload photo');
    }
    setUploadingPhoto(false);
    event.target.value = ''; // Reset file input
  };

  const handlePhotoDelete = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    
    try {
      const config = await getAuthConfig();
      await axios.delete(`${API_URL}/jobs/${id}/photos/${photoId}`, config);
      loadPhotos();
      setSelectedPhoto(null);
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete photo');
    }
  };

  const handlePhotoDownload = async (photo) => {
    try {
      const response = await fetch(`${API_BASE_URL || ''}${photo.url}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = photo.original_filename || `photo-${photo.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      // Revoking synchronously cancels the download on desktop Chrome/Edge, which
      // read the blob asynchronously - give them time before tearing it down.
      setTimeout(() => { a.remove(); window.URL.revokeObjectURL(url); }, 30000);
    } catch (error) {
      console.error('Error downloading photo:', error);
      alert('Failed to download photo');
    }
  };

  // Parcel Image handlers
  const handleParcelImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingParcel(true);
    try {
      const config = await getAuthConfig();
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      
      const response = await axios.post(`${API_URL}/jobs/${id}/parcel-image`, formDataUpload, config);
      // Update job state with new parcel image
      setJob(prev => ({
        ...prev,
        parcel_image: response.data.parcel_image,
        parcel_image_url: response.data.parcel_image_url
      }));
      setFormData(prev => ({
        ...prev,
        parcel_image: response.data.parcel_image,
        parcel_image_url: response.data.parcel_image_url
      }));
    } catch (error) {
      console.error('Error uploading parcel image:', error);
      alert('Failed to upload parcel image');
    }
    setUploadingParcel(false);
    event.target.value = '';
  };

  const handleParcelImageDelete = async () => {
    if (!window.confirm('Delete parcel image?')) return;
    
    try {
      const config = await getAuthConfig();
      await axios.delete(`${API_URL}/jobs/${id}/parcel-image`, config);
      setJob(prev => ({ ...prev, parcel_image: null, parcel_image_url: null }));
      setFormData(prev => ({ ...prev, parcel_image: null, parcel_image_url: null }));
    } catch (error) {
      console.error('Error deleting parcel image:', error);
      alert('Failed to delete parcel image');
    }
  };

  const handleSave = async () => {
    try {
      await updateJob(id, formData);
      setJob(formData);
      setEditing(false);
      loadJob(); // Reload to get updated data
    } catch (error) {
      console.error('Error updating job:', error);
      alert('Failed to update job');
    }
  };

  const handleAssigned = (updatedJob) => {
    setJob(updatedJob);
  };

  const handleBoundaryUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const allowedExtensions = ['kml', 'kmz', 'geojson'];
    const ext = file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(ext)) {
      alert('Invalid file type. Allowed: KML, KMZ, GeoJSON');
      return;
    }
    
    setUploadingBoundary(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/jobs/${id}/boundary`, formData, {
        ...config,
        headers: { ...config.headers, 'Content-Type': 'multipart/form-data' }
      });
      loadJob(); // Reload to get updated boundary_file
      alert('Boundary file uploaded successfully');
    } catch (error) {
      console.error('Error uploading boundary:', error);
      alert(error.response?.data?.error || 'Failed to upload boundary file');
    }
    setUploadingBoundary(false);
    event.target.value = ''; // Reset file input
  };

  const handleBoundaryDelete = async () => {
    if (!window.confirm('Delete this boundary file?')) return;
    
    try {
      const config = await getAuthConfig();
      await axios.delete(`${API_URL}/jobs/${id}/boundary`, config);
      loadJob();
    } catch (error) {
      console.error('Error deleting boundary:', error);
      alert('Failed to delete boundary file');
    }
  };

  // Map picker functions
  const searchAddressOnMap = async () => {
    if (!searchAddress.trim()) return;
    
    setSearching(true);
    try {
      let cleanAddress = searchAddress.trim().replace(/\s+/g, ' ');
      
      const searchVariations = [
        cleanAddress,
        cleanAddress.replace(/\s+(S\.?E\.?|N\.?E\.?|S\.?W\.?|N\.?W\.?|SE|NE|SW|NW)\s*/gi, ' '),
        cleanAddress.replace(/\s*\d{5}(-\d{4})?\s*$/, ''),
        cleanAddress.replace(/\s+(S\.?E\.?|N\.?E\.?|S\.?W\.?|N\.?W\.?|SE|NE|SW|NW)\s*/gi, ' ').replace(/\s*\d{5}(-\d{4})?\s*$/, '')
      ];
      
      const uniqueSearches = [...new Set(searchVariations.map(s => s.trim()))];
      let found = false;
      
      for (const searchTerm of uniqueSearches) {
        if (found) break;
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchTerm)}&limit=5`;
        const response = await fetch(url);
        const results = await response.json();
        
        if (results && results.length > 0) {
          const { lat, lon } = results[0];
          setMapCenter([parseFloat(lat), parseFloat(lon)]);
          setTempCoords({ lat: parseFloat(lat), lng: parseFloat(lon) });
          found = true;
        }
      }
      
      if (!found) {
        const parts = cleanAddress.split(',');
        if (parts.length >= 2) {
          const cityState = parts.slice(-2).join(',').trim().replace(/\s*\d{5}(-\d{4})?\s*$/, '');
          const fallbackUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cityState)}&limit=1`;
          const fallbackResponse = await fetch(fallbackUrl);
          const fallbackResults = await fallbackResponse.json();
          
          if (fallbackResults && fallbackResults.length > 0) {
            const { lat, lon } = fallbackResults[0];
            setMapCenter([parseFloat(lat), parseFloat(lon)]);
            alert('Exact address not found. Showing general area - zoom in and click to set location.');
          } else {
            alert('Address not found. Navigate manually and click on the map.');
          }
        }
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      alert('Error searching. Navigate manually and click on the map.');
    }
    setSearching(false);
  };

  const handleMapClick = (lat, lng) => {
    setTempCoords({ lat, lng });
  };

  const openMapPicker = () => {
    if (formData.latitude && formData.longitude) {
      const lat = parseFloat(formData.latitude);
      const lng = parseFloat(formData.longitude);
      setMapCenter([lat, lng]);
      setTempCoords({ lat, lng });
    } else if (formData.property_address) {
      setSearchAddress(formData.property_address);
    }
    setShowMapPicker(true);
  };

  const confirmMapSelection = () => {
    if (tempCoords.lat && tempCoords.lng) {
      setFormData(prev => ({
        ...prev,
        latitude: tempCoords.lat.toFixed(6),
        longitude: tempCoords.lng.toFixed(6)
      }));
    }
    setShowMapPicker(false);
    setTempCoords({ lat: null, lng: null });
    setSearchAddress('');
  };

  const cancelMapSelection = () => {
    setShowMapPicker(false);
    setTempCoords({ lat: null, lng: null });
    setSearchAddress('');
  };

  const statuses = [
    'Pending',
    'Scheduled',
    'In Progress - Office Work',
    'In Progress - Initial Field Work',
    'In Progress - Topo',
    'In Progress - Additional Field Work Required',
    'In Progress - Drafting',
    'Survey Complete',
    'Needs Review',
    'Sent for Review',
    'Approved - Internal Review',
    'Approved - Planning & Zoning',
    'Submitted for Recording',
    'Recorded',
    'Final Mark Up / Stake Lines',
    'Needs Invoice',
    'Invoiced',
    'Paid'
  ];

  // Keep a job's own retired status (the old bare 'Approved', 'Imported') selectable, so the
  // dropdown never renders blank and re-saving can't silently rewrite it.
  const statusChoices = job?.status && !statuses.includes(job.status)
    ? [...statuses, job.status]
    : statuses;

  if (loading) {
    return <div className="loading">Loading job...</div>;
  }

  if (!job) {
    return <div className="error">Job not found</div>;
  }

  return (
    <div className="job-detail-page" style={isMobile ? { padding: '15px', paddingBottom: '100px' } : {}}>
      <button className="btn btn-ghost" onClick={() => navigate(backPath)} style={isMobile ? { marginBottom: '10px' } : {}}>
        <FiArrowLeft /> Back to {backLabel}
      </button>

      <div className="detail-header" style={isMobile ? { flexDirection: 'column', alignItems: 'flex-start', gap: '10px' } : {}}>
        <div>
          <h1 style={isMobile ? { fontSize: '22px' } : {}}>{job.job_number}</h1>
          <p className="subtitle">{job.client_name}</p>
        </div>
        <div className="header-actions" style={isMobile ? { display: 'flex', flexWrap: 'wrap', gap: '8px', width: '100%' } : {}}>
          {editing ? (
            <>
              <button className="btn btn-secondary" onClick={() => setEditing(false)}>
                <FiX /> Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave}>
                <FiSave /> Save Changes
              </button>
            </>
          ) : (
            <>
              <button
                className="btn"
                onClick={() => navigate(`/jobs/${id}/v2`)}
                style={{ backgroundColor: '#6aa84f', color: 'white' }}
                title="Preview the new Qfactor-style job view"
              >
                <FiLayers /> New View
              </button>
              <button
                className="btn"
                onClick={() => setShowWorkOrder(true)}
                style={{ backgroundColor: '#059669', color: 'white' }}
              >
                <FiPrinter /> Print Work Order
              </button>
              <button className="btn btn-primary" onClick={() => setEditing(true)}>
                <FiEdit /> Edit Job
              </button>
              {canManagePayroll && job.status !== 'Cancelled' && job.status !== 'Paid' && (
                <button
                  className="btn"
                  onClick={() => setShowCancelModal(true)}
                  style={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}
                  title="Cancel this job (e.g., client backed out)"
                >
                  <FiX /> Cancel Job
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div style={{ margin: '4px 0 20px' }}>
        <JobTimer jobId={job.id} jobNumber={job.job_number} phases={job.has_phases ? job.phases : null} />
      </div>

      {/* Cancelled banner */}
      {job.status === 'Cancelled' && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px',
          padding: '16px 20px', marginBottom: '20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <FiX size={22} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <div style={{ fontWeight: 700, color: '#991b1b', fontSize: '15px' }}>
                This job has been cancelled
              </div>
              {job.cancelled_at && (
                <div style={{ fontSize: '12px', color: '#b91c1c', marginTop: '2px' }}>
                  Cancelled {new Date(job.cancelled_at + 'Z').toLocaleString()}
                </div>
              )}
              {job.cancelled_reason && (
                <div style={{ fontSize: '14px', color: '#7f1d1d', marginTop: '8px', whiteSpace: 'pre-wrap' }}>
                  <strong>Reason:</strong> {job.cancelled_reason}
                </div>
              )}
              {canManagePayroll && (
                <button
                  className="btn"
                  onClick={() => setShowReactivateModal(true)}
                  style={{ marginTop: '12px', backgroundColor: '#059669', color: 'white', border: 'none' }}
                >
                  <FiRefreshCw /> Reactivate &amp; Reschedule
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Job Modal */}
      {showCancelModal && (
        <CancelJobModal
          job={job}
          onClose={() => setShowCancelModal(false)}
          onCancelled={() => { setShowCancelModal(false); loadJob(); }}
        />
      )}

      {/* Reactivate Job Modal */}
      {showReactivateModal && (
        <ReactivateJobModal
          job={job}
          onClose={() => setShowReactivateModal(false)}
          onReactivated={() => { setShowReactivateModal(false); loadJob(); }}
        />
      )}

      {/* Client Contact Card */}
      <div style={{
        backgroundColor: '#f0f9ff',
        border: '1px solid #bae6fd',
        borderRadius: '12px',
        padding: isMobile ? '14px' : '16px',
        marginBottom: '20px'
      }}>
        <h3 style={{ 
          margin: '0 0 12px', 
          fontSize: '14px', 
          color: '#0369a1',
          fontWeight: '600'
        }}>
          📋 Client Contact
        </h3>
        <div style={{ fontWeight: '500', color: '#333', marginBottom: '12px' }}>
          {job.client_name}
        </div>
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: 'wrap', 
          gap: isMobile ? '10px' : '12px'
        }}>
          {job.client_phone && (
            <a
              href={`tel:${job.client_phone}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isMobile ? 'center' : 'flex-start',
                gap: '8px',
                padding: isMobile ? '14px 20px' : '10px 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                borderRadius: '10px',
                textDecoration: 'none',
                fontSize: isMobile ? '16px' : '14px',
                fontWeight: '500',
                minHeight: isMobile ? '48px' : 'auto'
              }}
            >
              <FiPhone size={isMobile ? 20 : 16} /> {job.client_phone}
            </a>
          )}
          
          {job.client_email && (
            <a
              href={`mailto:${job.client_email}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isMobile ? 'center' : 'flex-start',
                gap: '8px',
                padding: isMobile ? '14px 20px' : '10px 16px',
                backgroundColor: '#059669',
                color: 'white',
                borderRadius: '10px',
                textDecoration: 'none',
                fontSize: isMobile ? '16px' : '14px',
                fontWeight: '500',
                minHeight: isMobile ? '48px' : 'auto'
              }}
            >
              <FiMail size={isMobile ? 20 : 16} /> Email Client
            </a>
          )}
          
          {job.property_address && (
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(job.property_address)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isMobile ? 'center' : 'flex-start',
                gap: '8px',
                padding: isMobile ? '14px 20px' : '10px 16px',
                backgroundColor: '#7c3aed',
                color: 'white',
                borderRadius: '10px',
                textDecoration: 'none',
                fontSize: isMobile ? '16px' : '14px',
                fontWeight: '500',
                minHeight: isMobile ? '48px' : 'auto'
              }}
            >
              <FiMapPin size={isMobile ? 20 : 16} /> Directions
            </a>
          )}
        </div>
        
        {/* Location Person (on-site contact) */}
        {job.location_person && (
          <div style={{ 
            marginTop: '12px', 
            paddingTop: '12px', 
            borderTop: '1px solid #bae6fd',
            fontSize: '14px',
            color: '#555'
          }}>
            <strong>On-Site Contact:</strong> {job.location_person}
          </div>
        )}
      </div>

      {/* Qfactor-style consolidated overview (read-only, additive — shown when not editing) */}
      {!editing && (
        <JobOverview
          job={job}
          profitability={profitability}
          canManagePayroll={canManagePayroll}
          statuses={statusChoices}
          onUpdated={loadJob}
        />
      )}

      <div className="job-detail-content">
        {editing ? (
          <div className="edit-form">
            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="form-control"
              >
                {statusChoices.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Priority</label>
              <select
                value={formData.priority || 'Normal'}
                onChange={(e) => setFormData({...formData, priority: e.target.value})}
                className="form-control"
              >
                <option value="Low">Low</option>
                <option value="Normal">Normal</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Scheduled Date</label>
                <input
                  type="date"
                  value={formData.scheduled_date || ''}
                  onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Scheduled Time</label>
                <input
                  type="time"
                  value={formData.scheduled_time || ''}
                  onChange={(e) => setFormData({...formData, scheduled_time: e.target.value})}
                  className="form-control"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Property Address</label>
              <input
                type="text"
                value={formData.property_address || ''}
                onChange={(e) => setFormData({...formData, property_address: e.target.value})}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label>County</label>
              <input
                type="text"
                value={formData.county || ''}
                onChange={(e) => setFormData({...formData, county: e.target.value})}
                className="form-control"
              />
            </div>

            {/* Map Coordinates with Picker */}
            <div style={{ marginBottom: '15px' }}>
              <button
                type="button"
                onClick={openMapPicker}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                <FiMapPin /> Pick Location on Map
              </button>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Latitude</label>
                <input
                  type="number"
                  step="any"
                  value={formData.latitude || ''}
                  onChange={(e) => setFormData({...formData, latitude: e.target.value})}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Longitude</label>
                <input
                  type="number"
                  step="any"
                  value={formData.longitude || ''}
                  onChange={(e) => setFormData({...formData, longitude: e.target.value})}
                  className="form-control"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows="4"
                className="form-control"
              />
            </div>

            {/* Parcel Image Section */}
            <div className="form-group" style={{ marginTop: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <FiImage /> Parcel Image (for Work Order)
              </label>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                This image will appear on printed work orders.
              </p>
              
              {(job?.parcel_image_url || formData?.parcel_image_url) ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '15px' }}>
                  <div style={{
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    maxWidth: '300px'
                  }}>
                    <img 
                      src={`${API_URL}${job?.parcel_image_url || formData?.parcel_image_url}`}
                      alt="Parcel" 
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      backgroundColor: '#1976d2',
                      color: 'white',
                      borderRadius: '6px',
                      cursor: uploadingParcel ? 'not-allowed' : 'pointer',
                      fontSize: '13px'
                    }}>
                      <FiUpload /> Replace
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleParcelImageUpload}
                        disabled={uploadingParcel}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={handleParcelImageDelete}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      <FiTrash2 /> Remove
                    </button>
                  </div>
                </div>
              ) : (
                <label style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed #ccc',
                  borderRadius: '8px',
                  padding: '30px 20px',
                  cursor: uploadingParcel ? 'not-allowed' : 'pointer',
                  backgroundColor: '#f9f9f9',
                  maxWidth: '300px'
                }}>
                  <FiImage size={40} color="#999" />
                  <span style={{ marginTop: '10px', color: '#666' }}>
                    {uploadingParcel ? 'Uploading...' : 'Click to upload parcel image'}
                  </span>
                  <span style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                    PNG, JPG, GIF, WEBP
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleParcelImageUpload}
                    disabled={uploadingParcel}
                    style={{ display: 'none' }}
                  />
                </label>
              )}
            </div>
          </div>
        ) : (
          <div className="job-info-grid">
            <div className="info-card">
              <label>Status</label>
              <p style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: job.status === 'Pending' ? '#fef3c7' :
                                 job.status === 'Scheduled' ? '#dbeafe' :
                                 job.status === 'In Progress - Initial Field Work' ? '#e9d5ff' :
                                 job.status === 'In Progress - Topo' ? '#e0e7ff' :
                                 job.status === 'In Progress - Additional Field Work Required' ? '#ddd6fe' :
                                 job.status === 'In Progress - Drafting' ? '#ede9fe' :
                                 job.status === 'Survey Complete' ? '#d1fae5' :
                                 job.status === 'Needs Review' ? '#ffedd5' :
                                 job.status === 'Sent for Review' ? '#fef3c7' :
                                 job.status === 'Approved' ? '#dcfce7' :
                                 job.status === 'Approved - Internal Review' ? '#dcfce7' :
                                 job.status === 'Approved - Planning & Zoning' ? '#bbf7d0' :
                                 job.status === 'Submitted for Recording' ? '#ccfbf1' :
                                 job.status === 'Recorded' ? '#99f6e4' :
                                 job.status === 'Final Mark Up / Stake Lines' ? '#fce7f3' :
                                 job.status === 'Needs Invoice' ? '#fef3c7' :
                                 job.status === 'Invoiced' ? '#fed7aa' :
                                 job.status === 'Paid' ? '#bbf7d0' : '#e5e7eb',
                color: job.status === 'Pending' ? '#92400e' :
                       job.status === 'Scheduled' ? '#1e40af' :
                       job.status === 'In Progress - Initial Field Work' ? '#6b21a8' :
                       job.status === 'In Progress - Topo' ? '#4338ca' :
                       job.status === 'In Progress - Additional Field Work Required' ? '#5b21b6' :
                       job.status === 'In Progress - Drafting' ? '#6d28d9' :
                       job.status === 'Survey Complete' ? '#065f46' :
                       job.status === 'Needs Review' ? '#c2410c' :
                       job.status === 'Sent for Review' ? '#92400e' :
                       job.status === 'Approved' ? '#166534' :
                       job.status === 'Approved - Internal Review' ? '#166534' :
                       job.status === 'Approved - Planning & Zoning' ? '#14532d' :
                       job.status === 'Submitted for Recording' ? '#0f766e' :
                       job.status === 'Recorded' ? '#115e59' :
                       job.status === 'Final Mark Up / Stake Lines' ? '#be185d' :
                       job.status === 'Needs Invoice' ? '#92400e' :
                       job.status === 'Invoiced' ? '#c2410c' :
                       job.status === 'Paid' ? '#166534' : '#374151'
              }}>
                {job.status || 'No Status'}
              </p>
            </div>

            {job.priority && job.priority !== 'Normal' && (
              <div className="info-card">
                <label>Priority</label>
                <p style={{
                  display: 'inline-block', padding: '4px 12px', borderRadius: '4px',
                  fontSize: '13px', fontWeight: '700', textTransform: 'uppercase',
                  color: job.priority === 'Urgent' ? '#dc2626' : job.priority === 'High' ? '#ea580c' : '#6b7280',
                  backgroundColor: job.priority === 'Urgent' ? '#fef2f2' : job.priority === 'High' ? '#fff7ed' : '#f9fafb',
                  border: `1px solid ${job.priority === 'Urgent' ? '#fecaca' : job.priority === 'High' ? '#fed7aa' : '#e5e7eb'}`
                }}>
                  {job.priority}
                </p>
              </div>
            )}

            {job.scheduled_date && (
              <div className="info-card">
                <label>Scheduled</label>
                <p>
                  {new Date(job.scheduled_date + 'T00:00:00').toLocaleDateString()}
                  {job.scheduled_time && ` at ${job.scheduled_time}`}
                </p>
              </div>
            )}

            {job.last_worked_date && (
              <div className="info-card">
                <label>Last Worked</label>
                <p>
                  {new Date(job.last_worked_date).toLocaleDateString()}
                  {job.last_worked_by && <span style={{ color: '#6b7280', fontSize: '13px' }}> by {job.last_worked_by}</span>}
                </p>
              </div>
            )}

            {job.property_address && (
              <div className="info-card full-width">
                <label>Property Address</label>
                <p>{job.property_address}</p>
              </div>
            )}

            {(job.latitude && job.longitude) && (
              <div className="info-card">
                <label>Coordinates</label>
                <p>{job.latitude}, {job.longitude}</p>
              </div>
            )}

            {job.notes && (
              <div className="info-card full-width">
                <label>Notes</label>
                <p>{job.notes}</p>
              </div>
            )}

            <div className="info-card">
              <label>Created</label>
              <p>{parseUTCDate(job.created_at)?.toLocaleString()}</p>
            </div>

            <div className="info-card">
              <label>Last Updated</label>
              <p>{parseUTCDate(job.updated_at)?.toLocaleString()}</p>
            </div>

          </div>
        )}

        {/* Time Tracking Section */}
        <div style={{
          marginTop: isMobile ? '1rem' : '2rem',
          padding: isMobile ? '1rem' : '1.5rem',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div
            onClick={() => setShowTimeTracking(!showTimeTracking)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
            <h2 style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              margin: 0,
              color: '#333',
              fontSize: isMobile ? '18px' : '24px'
            }}>
              <FiClock /> Time Tracking
              <span style={{ fontSize: '14px', color: '#999', marginLeft: '4px' }}>{showTimeTracking ? '▲' : '▼'}</span>
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                backgroundColor: '#e3f2fd',
                padding: '8px 16px',
                borderRadius: '20px',
                fontWeight: 'bold',
                color: '#1976d2',
                fontSize: isMobile ? '14px' : '16px'
              }}>
                Total: {totalJobHours.toFixed(2)}h
              </div>
              {!showTimeTracking && jobTimeEntries.length > 0 && (
                <span style={{ fontSize: '13px', color: '#999' }}>{jobTimeEntries.length} entries</span>
              )}
            </div>
          </div>

          {showTimeTracking && jobTimeEntries.length > 0 ? (
            isMobile ? (
              /* Mobile Card View */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '1rem' }}>
                {jobTimeEntries.map(entry => (
                  <div 
                    key={entry.id} 
                    style={{
                      backgroundColor: '#f9fafb',
                      borderRadius: '10px',
                      padding: '12px'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <strong style={{ fontSize: '15px' }}>{entry.team_member_name}</strong>
                      <span style={{ 
                        fontWeight: '600', 
                        fontSize: '16px',
                        color: '#1976d2'
                      }}>
                        {entry.total_hours ? `${entry.total_hours.toFixed(2)}h` : '—'}
                      </span>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '13px',
                      color: '#666'
                    }}>
                      <span>
                        {parseUTCDate(entry.clock_in)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {' • '}
                        {parseUTCDate(entry.clock_in)?.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                        {' → '}
                        {entry.clock_out 
                          ? parseUTCDate(entry.clock_out)?.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                          : <span style={{ color: '#4caf50' }}>Active</span>
                        }
                      </span>
                      <span style={{
                        backgroundColor: '#e5e7eb',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px'
                      }}>
                        {entry.work_type}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Desktop Table View */
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                      <th style={{ textAlign: 'left', padding: '10px', color: '#666' }}>Team Member</th>
                      <th style={{ textAlign: 'left', padding: '10px', color: '#666' }}>Date</th>
                      <th style={{ textAlign: 'left', padding: '10px', color: '#666' }}>Clock In</th>
                      <th style={{ textAlign: 'left', padding: '10px', color: '#666' }}>Clock Out</th>
                      <th style={{ textAlign: 'right', padding: '10px', color: '#666' }}>Hours</th>
                      <th style={{ textAlign: 'left', padding: '10px', color: '#666' }}>Work Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobTimeEntries.map(entry => (
                      <tr key={entry.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px 10px' }}>
                          <strong>{entry.team_member_name}</strong>
                        </td>
                        <td style={{ padding: '12px 10px' }}>
                          {parseUTCDate(entry.clock_in)?.toLocaleDateString()}
                        </td>
                        <td style={{ padding: '12px 10px' }}>
                          {parseUTCDate(entry.clock_in)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td style={{ padding: '12px 10px' }}>
                          {entry.clock_out 
                            ? parseUTCDate(entry.clock_out)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : <span style={{ color: '#4caf50', fontWeight: '500' }}>Active</span>
                          }
                        </td>
                        <td style={{ padding: '12px 10px', textAlign: 'right', fontWeight: '500' }}>
                          {entry.total_hours ? entry.total_hours.toFixed(2) : '—'}
                        </td>
                        <td style={{ padding: '12px 10px' }}>
                          <span style={{
                            backgroundColor: '#f5f5f5',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            {entry.work_type}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <td colSpan="4" style={{ padding: '12px 10px', fontWeight: 'bold' }}>
                        Total Time on Job
                      </td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', fontWeight: 'bold', color: '#1976d2' }}>
                        {totalJobHours.toFixed(2)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )
          ) : showTimeTracking ? (
            <div style={{
              textAlign: 'center',
              padding: isMobile ? '30px 15px' : '40px 20px',
              color: '#999',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              marginTop: '1rem'
            }}>
              <FiClock size={32} style={{ marginBottom: '10px', opacity: 0.5 }} />
              <p style={{ margin: 0 }}>No time entries for this job yet</p>
              <p style={{ margin: '5px 0 0', fontSize: '13px' }}>
                Use the Time Clock to track time on this job
              </p>
            </div>
          ) : null}
        </div>

        {/* Field Departure Checklists */}
        <div style={{
          marginTop: '2rem', padding: '1.5rem', background: 'white',
          borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div
            onClick={() => setShowFieldChecklists(!showFieldChecklists)}
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              cursor: 'pointer', userSelect: 'none'
            }}
          >
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0, fontSize: '20px' }}>
              <FiCheckSquare /> Field Checklists
              {fieldChecklists.length > 0 && (
                <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '400' }}>
                  ({fieldChecklists.length})
                </span>
              )}
            </h2>
            <FiChevronDown style={{
              transform: showFieldChecklists ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s', color: '#6b7280'
            }} />
          </div>

          {showFieldChecklists && (
            <div style={{ marginTop: '16px' }}>
              {/* New Checklist button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                <button
                  onClick={async () => {
                    try {
                      const config = await getAuthConfig();
                      const name = prompt('Checklist name (e.g., "Phase 2 - Foundation As-Built"):') || undefined;
                      await axios.post(`${API_URL}/field-checklists/create`, {
                        job_id: id, checklist_type: 'boundary', name
                      }, config);
                      loadFieldChecklists();
                    } catch (err) { alert('Failed to create checklist'); }
                  }}
                  style={{
                    padding: '8px 14px', backgroundColor: '#2563eb', color: 'white',
                    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                    fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px'
                  }}
                >
                  <FiPlus size={14} /> New Checklist
                </button>
              </div>

              {/* Field checklists list */}
              {fieldChecklists.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '13px' }}>
                  No field checklists yet for this job.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {fieldChecklists.map(fc => {
                    const isActive = fc.is_active && !fc.all_complete;
                    return (
                      <div
                        key={fc.id}
                        onClick={() => setEditingFieldChecklist({ id: fc.id, job_id: job.id, job_number: job.job_number })}
                        style={{
                          border: `2px solid ${isActive ? '#2563eb' : fc.all_complete ? '#86efac' : '#e5e7eb'}`,
                          borderRadius: '8px', padding: '12px', cursor: 'pointer',
                          backgroundColor: isActive ? '#eff6ff' : fc.all_complete ? '#f0fdf4' : '#fef3c7',
                        }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <div>
                            <strong style={{ fontSize: '14px' }}>{fc.name || 'Field Checklist'}</strong>
                            <span style={{ color: '#6b7280', fontSize: '12px', marginLeft: '8px' }}>
                              {fc.completed_by_name && `by ${fc.completed_by_name}`}
                              {fc.created_at && ` · ${new Date(fc.created_at).toLocaleDateString()}`}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <span style={{
                              padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                              backgroundColor: isActive ? '#dbeafe' : fc.all_complete ? '#dcfce7' : '#fef3c7',
                              color: isActive ? '#1e40af' : fc.all_complete ? '#166534' : '#92400e',
                            }}>
                              {isActive ? 'In Progress' : fc.all_complete ? 'Complete' : 'Incomplete'}
                            </span>
                            {canManagePayroll && (
                              <button onClick={async () => {
                                if (!window.confirm('Delete this checklist?')) return;
                                try {
                                  const config = await getAuthConfig();
                                  await axios.delete(`${API_URL}/field-checklists/${fc.id}`, config);
                                  loadFieldChecklists();
                                } catch (err) { alert('Failed to delete'); }
                              }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '2px' }}>
                                <FiTrash2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Progress bar */}
                        <div style={{ marginBottom: '6px' }}>
                          <div style={{ height: '6px', backgroundColor: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{
                              height: '100%', borderRadius: '3px',
                              width: `${fc.totals?.total ? Math.round(((fc.totals.checked + fc.totals.na) / fc.totals.total) * 100) : 0}%`,
                              backgroundColor: fc.all_complete ? '#16a34a' : '#2563eb',
                            }} />
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                            {fc.totals?.checked || 0} checked · {fc.totals?.na || 0} N/A · {fc.totals?.unchecked || 0} remaining
                          </div>
                        </div>
                        {/* Show items with notes */}
                        {Object.entries(fc.categories || {}).map(([cat, items]) => {
                          const notedItems = items.filter(i => i.note);
                          if (notedItems.length === 0) return null;
                          return notedItems.map(item => (
                            <div key={item.id} style={{
                              marginTop: '4px', padding: '6px 8px', backgroundColor: 'white',
                              borderRadius: '4px', fontSize: '12px', border: '1px solid #e5e7eb'
                            }}>
                              <strong>{item.item_text.length > 60 ? item.item_text.slice(0, 60) + '...' : item.item_text}</strong>
                              <div style={{ color: '#2563eb', marginTop: '2px' }}>Note: {item.note}</div>
                            </div>
                          ));
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Team Members Section */}
        {/* Related Jobs (Parent/Child) */}
        {(job.parent_job_id || (job.child_jobs && job.child_jobs.length > 0)) && (
          <div style={{
            marginTop: '2rem', padding: '1.5rem', background: 'white',
            borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 1rem', color: '#333', fontSize: '20px' }}>
              <FiLink /> Related Jobs
            </h2>

            {job.parent_job_id && (
              <div style={{ marginBottom: '12px' }}>
                <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Parent Job:</span>
                <Link to={`/jobs/${job.parent_job_id}`} style={{
                  display: 'block', marginTop: '4px', padding: '10px 14px',
                  backgroundColor: '#eff6ff', border: '1px solid #bfdbfe',
                  borderRadius: '8px', textDecoration: 'none', color: '#1e40af',
                  fontWeight: '600', fontSize: '15px'
                }}>
                  {job.parent_job_number}
                </Link>
              </div>
            )}

            {job.child_jobs && job.child_jobs.length > 0 && (
              <div>
                <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>
                  Sub-Jobs ({job.child_jobs.length}):
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
                  {job.child_jobs.map(child => (
                    <Link key={child.id} to={`/jobs/${child.id}`} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 14px', backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb', borderRadius: '8px',
                      textDecoration: 'none', color: '#374151'
                    }}>
                      <div>
                        <strong style={{ color: '#2563eb' }}>{child.job_number}</strong>
                        {child.property_address && <span style={{ color: '#6b7280', marginLeft: '8px', fontSize: '13px' }}>{child.property_address}</span>}
                      </div>
                      <span style={{
                        padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
                        backgroundColor: child.status === 'Paid' ? '#dcfce7' : child.status?.includes('Drafting') ? '#ede9fe' : '#dbeafe',
                        color: child.status === 'Paid' ? '#166534' : child.status?.includes('Drafting') ? '#6d28d9' : '#1e40af'
                      }}>
                        {child.status}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="team-section">
          <h2><FiUsers /> Team Members</h2>
          
          {job.team_members && job.team_members.length > 0 ? (
            <div className="assigned-team-grid">
              {job.team_members.map(member => (
                <div key={member.id} className="team-member-card">
                  <div className="member-avatar">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="member-info">
                    <div className="member-name">{member.name}</div>
                    <div className="member-role">{member.role}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-team-members">No team members assigned yet</p>
          )}

          <div className="self-assign-section">
            <SelfAssignButton
              job={job}
              teamMemberId={user?.id}
              onAssigned={handleAssigned}
            />
          </div>
        </div>

        {/* Job Contacts Section */}
        <div style={{
          marginTop: '2rem', padding: '1.5rem', background: 'white',
          borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div
            onClick={() => setShowContacts(!showContacts)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          >
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0, fontSize: '20px' }}>
              <FiUsers /> Job Contacts
              <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '400' }}>
                ({jobContacts.length})
              </span>
            </h2>
            <FiChevronDown style={{ transform: showContacts ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', color: '#6b7280' }} />
          </div>

          {showContacts && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px' }}>
                All contacts will be notified on En Route, status changes, and portal updates.
              </p>

              {/* Pull people from the client company's saved roster instead of retyping
                  them. Field and status people are added automatically when a job is
                  created; this is for adding anyone else (or fixing an opt-out). */}
              {!showRosterPicker && (
                <button onClick={openRosterPicker} style={{
                  padding: '7px 12px', backgroundColor: 'white', color: '#2563eb',
                  border: '1px solid #2563eb', borderRadius: '6px', cursor: 'pointer',
                  fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center',
                  gap: '6px', marginBottom: '12px',
                }}>
                  <FiUsers size={14} /> Add from Company
                </button>
              )}

              {showRosterPicker && (
                <div style={{
                  padding: '12px', marginBottom: '16px', backgroundColor: '#f8fafc',
                  border: '1px solid #bfdbfe', borderRadius: '8px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                      {roster.client_label ? `Contacts at ${roster.client_label}` : 'Company Contacts'}
                    </div>
                    <button onClick={() => setShowRosterPicker(false)} style={{
                      background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '2px',
                    }} title="Close">
                      <FiX size={16} />
                    </button>
                  </div>

                  {rosterBusy && roster.candidates.length === 0 ? (
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>Loading…</div>
                  ) : roster.candidates.length === 0 ? (
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      No saved contacts for this client yet. Add them on the client's page and
                      they'll be available on every job.
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                        {roster.candidates.map(rc => {
                          const disabled = rc.already_added || rc.is_primary_client;
                          const why = rc.is_primary_client ? 'Already the job’s primary client'
                            : rc.already_added ? 'Already on this job' : null;
                          const purposes = [
                            rc.receives_billing && 'Billing',
                            rc.receives_field && 'Field',
                            rc.receives_status && 'Status',
                          ].filter(Boolean);
                          return (
                            <label key={rc.id} style={{
                              display: 'flex', alignItems: 'flex-start', gap: '8px',
                              padding: '8px 10px', backgroundColor: 'white',
                              border: '1px solid #e5e7eb', borderRadius: '6px',
                              cursor: disabled ? 'not-allowed' : 'pointer',
                              opacity: disabled ? 0.55 : 1,
                            }}>
                              <input
                                type="checkbox"
                                disabled={disabled}
                                checked={rosterPicked.includes(rc.id)}
                                onChange={() => toggleRosterPick(rc.id)}
                                style={{ marginTop: '3px' }}
                              />
                              <span style={{ minWidth: 0 }}>
                                <span style={{ fontSize: '13px', fontWeight: '600' }}>{rc.name}</span>
                                {rc.title && (
                                  <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '400' }}>
                                    {' '}— {rc.title}
                                  </span>
                                )}
                                <span style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                  {[rc.phone, rc.email].filter(Boolean).join('  ·  ')}
                                </span>
                                {(purposes.length > 0 || why) && (
                                  <span style={{ display: 'flex', gap: '5px', marginTop: '5px', flexWrap: 'wrap' }}>
                                    {purposes.map(p => (
                                      <span key={p} style={{
                                        fontSize: '10px', fontWeight: '600', textTransform: 'uppercase',
                                        color: '#374151', backgroundColor: '#f3f4f6',
                                        borderRadius: '999px', padding: '2px 7px',
                                      }}>{p}</span>
                                    ))}
                                    {why && (
                                      <span style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>
                                        {why}
                                      </span>
                                    )}
                                  </span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      <button
                        onClick={handleAddFromRoster}
                        disabled={rosterPicked.length === 0 || rosterBusy}
                        style={{
                          padding: '8px 14px',
                          backgroundColor: rosterPicked.length > 0 && !rosterBusy ? '#2563eb' : '#9ca3af',
                          color: 'white', border: 'none', borderRadius: '6px',
                          cursor: rosterPicked.length > 0 && !rosterBusy ? 'pointer' : 'not-allowed',
                          fontSize: '13px', fontWeight: '600', display: 'flex',
                          alignItems: 'center', gap: '6px',
                        }}
                      >
                        <FiPlus size={14} />
                        {rosterBusy ? 'Adding…'
                          : rosterPicked.length > 0 ? `Add ${rosterPicked.length} to This Job`
                            : 'Select someone to add'}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Existing contacts */}
              {jobContacts.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                  {jobContacts.map(c => {
                    const isEditing = !c.is_primary && c.id != null && editingContact === c.id;
                    return (
                      <div key={c.is_primary ? 'primary' : c.id} style={{
                        padding: '10px 12px', border: `1px solid ${c.is_primary ? '#bfdbfe' : isEditing ? '#2563eb' : '#e5e7eb'}`,
                        borderRadius: '8px', backgroundColor: c.is_primary ? '#eff6ff' : isEditing ? '#f0f9ff' : 'white',
                      }}>
                        {isEditing ? (
                          /* Edit mode */
                          <div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '8px' }}>
                              <input type="text" value={editContactForm.name}
                                onChange={(e) => setEditContactForm(p => ({ ...p, name: e.target.value }))}
                                placeholder="Name *"
                                style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} />
                              <input type="text" value={editContactForm.role}
                                onChange={(e) => setEditContactForm(p => ({ ...p, role: e.target.value }))}
                                placeholder="Role"
                                style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} />
                              <input type="tel" value={editContactForm.phone}
                                onChange={(e) => setEditContactForm(p => ({ ...p, phone: e.target.value }))}
                                placeholder="Phone"
                                style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} />
                              <input type="email" value={editContactForm.email}
                                onChange={(e) => setEditContactForm(p => ({ ...p, email: e.target.value }))}
                                placeholder="Email"
                                style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} />
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button onClick={handleSaveContact} style={{
                                padding: '6px 12px', backgroundColor: '#2563eb', color: 'white',
                                border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
                              }}>Save</button>
                              <button onClick={() => setEditingContact(null)} style={{
                                padding: '6px 12px', backgroundColor: '#f5f5f5', border: '1px solid #ddd',
                                borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
                              }}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          /* View mode */
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {c.name}
                                {c.is_primary && (
                                  <span style={{ fontSize: '10px', padding: '2px 6px', backgroundColor: '#2563eb', color: 'white', borderRadius: '4px' }}>
                                    PRIMARY
                                  </span>
                                )}
                                {c.role && (
                                  <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '400' }}>
                                    — {c.role}
                                  </span>
                                )}
                                {/* Explains why someone showed up here without anyone typing
                                    them in: they're on the client company's saved roster. */}
                                {c.client_contact_id && (
                                  <span title="Saved on the client company's contact list" style={{
                                    fontSize: '10px', padding: '2px 6px', backgroundColor: '#f3f4f6',
                                    color: '#6b7280', borderRadius: '4px', fontWeight: '600',
                                  }}>
                                    COMPANY
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', display: 'flex', gap: '12px' }}>
                                {c.phone && <span><FiPhone size={11} /> {c.phone}</span>}
                                {c.email && <span><FiMail size={11} /> {c.email}</span>}
                              </div>
                            </div>
                            {!c.is_primary && (
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button
                                  onClick={() => { setEditingContact(c.id); setEditContactForm({ name: c.name || '', phone: c.phone || '', email: c.email || '', role: c.role || '' }); }}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', padding: '4px' }}
                                  title="Edit contact"
                                >
                                  <FiEdit size={14} />
                                </button>
                                <button
                                  onClick={() => handleDeleteContact(c.id)}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '4px' }}
                                  title="Remove contact"
                                >
                                  <FiTrash2 size={14} />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add contact form */}
              <div style={{
                padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px',
                border: '1px solid #e5e7eb',
              }}>
                <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  Add Contact
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text" value={newContact.name}
                    onChange={(e) => setNewContact(p => ({ ...p, name: e.target.value }))}
                    placeholder="Name *"
                    style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                  />
                  <input
                    type="text" value={newContact.role}
                    onChange={(e) => setNewContact(p => ({ ...p, role: e.target.value }))}
                    placeholder="Role (e.g., Site Foreman)"
                    style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                  />
                  <input
                    type="tel" value={newContact.phone}
                    onChange={(e) => setNewContact(p => ({ ...p, phone: e.target.value }))}
                    placeholder="Phone"
                    style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                  />
                  <input
                    type="email" value={newContact.email}
                    onChange={(e) => setNewContact(p => ({ ...p, email: e.target.value }))}
                    placeholder="Email"
                    style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
                <button onClick={handleAddContact} disabled={!newContact.name.trim()} style={{
                  padding: '8px 14px', backgroundColor: newContact.name.trim() ? '#2563eb' : '#9ca3af',
                  color: 'white', border: 'none', borderRadius: '6px',
                  cursor: newContact.name.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                  <FiPlus size={14} /> Add Contact
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Client Portal Section */}
        <div style={{
          marginTop: '2rem', padding: '1.5rem', background: 'white',
          borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div
            onClick={() => setShowPortal(!showPortal)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          >
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0, fontSize: '20px' }}>
              <FiGlobe /> Client Portal
              {portalData?.is_active && (
                <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: '400' }}>(Active)</span>
              )}
            </h2>
            <FiChevronDown style={{ transform: showPortal ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', color: '#6b7280' }} />
          </div>

          {showPortal && portalData && (
            <div style={{ marginTop: '16px' }}>
              {/* Portal Link */}
              <div style={{
                padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px',
                border: '1px solid #bfdbfe', marginBottom: '12px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px',
                flexWrap: 'wrap',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>Client Portal Link</div>
                  <div style={{ fontSize: '13px', color: '#2563eb', wordBreak: 'break-all' }}>
                    {`${window.location.origin}/portal/${portalData.token}`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/portal/${portalData.token}`); alert('Link copied!'); }}
                    style={{
                      padding: '8px 12px', backgroundColor: '#2563eb', color: 'white',
                      border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
                      display: 'flex', alignItems: 'center', gap: '4px',
                    }}
                  >
                    <FiCopy size={12} /> Copy
                  </button>
                  <button
                    onClick={handleTogglePortal}
                    style={{
                      padding: '8px 12px', backgroundColor: portalData.is_active ? '#fef2f2' : '#f0fdf4',
                      color: portalData.is_active ? '#dc2626' : '#16a34a',
                      border: `1px solid ${portalData.is_active ? '#fecaca' : '#86efac'}`,
                      borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
                    }}
                  >
                    {portalData.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>

              {/* Quick Actions */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                {/* En Route — posts to portal AND opens SMS */}
                <button onClick={async () => {
                  await handleEnRoute();
                  const phone = portalData?.client?.phone;
                  const portalUrl = `${window.location.origin}/portal/${portalData.token}`;
                  const msg = encodeURIComponent(
                    `Hi${portalData?.client?.name ? ' ' + portalData.client.name.split(' ')[0] : ''}, this is ${user?.name || 'the crew'} from North Georgia Surveying. We're headed to your property now. Track your project here: ${portalUrl}`
                  );
                  if (phone) {
                    window.open(`sms:${phone}?body=${msg}`, '_blank');
                  }
                }} style={{
                  padding: '10px 16px', backgroundColor: '#16a34a', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px',
                  fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                  <FiTruck size={14} /> En Route — Text Client
                </button>

                {/* Text Portal Link */}
                {portalData?.client?.phone && (
                  <a
                    href={`sms:${portalData.client.phone}?body=${encodeURIComponent(`North Georgia Surveying, Inc.\n\nHi${portalData?.client?.name ? ' ' + portalData.client.name.split(' ')[0] : ''}, here is your project portal for job ${job.job_number}. You can view your project status, schedule updates, and communicate with our team at any time.\n\nYour Portal: ${window.location.origin}/portal/${portalData.token}\n\nQuestions? Call us at 706-625-1046`)}`}
                    style={{
                      padding: '10px 16px', backgroundColor: '#eff6ff', color: '#2563eb',
                      border: '1px solid #bfdbfe', borderRadius: '8px', fontSize: '13px',
                      fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
                      textDecoration: 'none',
                    }}
                  >
                    <FiPhone size={14} /> Text Portal Link
                  </a>
                )}

                {/* Email Portal Link */}
                {jobContacts.some(c => c.email) && (
                  <button
                    onClick={async () => {
                      const emailable = jobContacts.filter(c => c.email);
                      const count = emailable.length;
                      const preview = emailable.slice(0, 5).map(c => `${c.name} (${c.email})`).join('\n');
                      const more = count > 5 ? `\n...and ${count - 5} more` : '';
                      if (!window.confirm(`Email the portal link to ${count} contact${count !== 1 ? 's' : ''}?\n\n${preview}${more}`)) return;
                      try {
                        const config = await getAuthConfig();
                        const res = await axios.post(`${API_URL}/jobs/${id}/portal/email-link`, {}, config);
                        alert(res.data?.message || `Portal link emailed to ${count} contact(s)`);
                      } catch (err) {
                        alert(err.response?.data?.error || 'Failed to send email');
                      }
                    }}
                    style={{
                      padding: '10px 16px', backgroundColor: '#f9fafb', color: '#374151',
                      border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px',
                      fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    <FiMail size={14} /> Email Portal Link
                  </button>
                )}

                {/* Notify Selected Contacts */}
                <button
                  onClick={() => {
                    setNotifySelection(jobContacts.map((_, i) => i));
                    setNotifyMessage({ title: '', message: '', send_sms: true, send_email: true });
                    setShowNotifyModal(true);
                  }}
                  style={{
                    padding: '10px 16px', backgroundColor: '#7c3aed', color: 'white',
                    border: 'none', borderRadius: '8px', fontSize: '13px',
                    fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
                    cursor: 'pointer',
                  }}
                >
                  <FiSend size={14} /> Notify Contacts
                </button>
              </div>

              {!portalData?.client?.phone && !portalData?.client?.email && (
                <div style={{ fontSize: '12px', color: '#d97706', marginBottom: '12px', padding: '8px', backgroundColor: '#fffbeb', borderRadius: '6px' }}>
                  No phone or email on file for this client. Add contact info to the client record to enable texting and emailing.
                </div>
              )}

              {/* Weekly digest settings */}
              <div style={{
                padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px',
                border: '1px solid #bae6fd', marginBottom: '12px',
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!portalData.weekly_digest_enabled}
                    onChange={async (e) => {
                      try {
                        const config = await getAuthConfig();
                        await axios.patch(`${API_URL}/jobs/${id}/portal/digest-settings`, { weekly_digest_enabled: e.target.checked }, config);
                        loadPortal();
                      } catch (err) {
                        alert('Failed to update weekly digest setting');
                      }
                    }}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#0c4a6e' }}>
                      Weekly status updates
                    </div>
                    <div style={{ fontSize: '11px', color: '#0369a1', marginTop: '2px' }}>
                      Monday mornings: post a friendly status update to the portal and email/text the client. Skips weeks with no activity (after 2 quiet weeks, sends a reassurance).
                    </div>
                  </div>
                </label>
                {portalData.last_digest_sent_at && (
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '8px' }}>
                    Last digest sent: {new Date(portalData.last_digest_sent_at + 'Z').toLocaleString()}
                  </div>
                )}
                <button
                  onClick={async () => {
                    if (!window.confirm('Send the weekly digest to this client right now? (Only for testing — the cron runs Monday at 8am automatically.)')) return;
                    try {
                      const config = await getAuthConfig();
                      const res = await axios.post(`${API_URL}/cron/weekly-digest`, {}, config);
                      alert(`Digest run complete:\nstatus_update: ${res.data.status_update}\nreassurance: ${res.data.reassurance}\nskipped: ${res.data.skipped}\nerrors: ${res.data.errors}`);
                      loadPortal();
                    } catch (err) {
                      alert(err.response?.data?.error || 'Failed to run digest');
                    }
                  }}
                  style={{
                    marginTop: '8px',
                    padding: '6px 12px', fontSize: '11px',
                    backgroundColor: 'white', color: '#0369a1',
                    border: '1px solid #bae6fd', borderRadius: '6px', cursor: 'pointer',
                  }}
                >
                  Send Digest Now (test)
                </button>
              </div>

              {/* Send Message */}
              <div style={{
                padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px',
                border: '1px solid #e5e7eb', marginBottom: '12px',
              }}>
                <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  Send Client a Message
                </div>
                <input
                  type="text" value={portalMessage.title}
                  onChange={(e) => setPortalMessage(p => ({ ...p, title: e.target.value }))}
                  placeholder="Title (e.g., 'Schedule Update')"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', marginBottom: '6px', boxSizing: 'border-box' }}
                />
                <textarea
                  value={portalMessage.message}
                  onChange={(e) => setPortalMessage(p => ({ ...p, message: e.target.value }))}
                  placeholder="Message (optional)"
                  rows={2}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', marginBottom: '6px', boxSizing: 'border-box', resize: 'vertical' }}
                />
                <button onClick={handleSendPortalMessage} disabled={!portalMessage.title.trim()} style={{
                  padding: '8px 14px', backgroundColor: portalMessage.title.trim() ? '#2563eb' : '#9ca3af',
                  color: 'white', border: 'none', borderRadius: '6px', cursor: portalMessage.title.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                  <FiSend size={12} /> Send
                </button>
              </div>

              {/* Recent Notifications */}
              {portalNotifications.length > 0 && (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                    Recent Client Updates ({portalNotifications.length})
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {portalNotifications.slice(0, 20).map(n => {
                      const isClientRequest = n.notification_type === 'client_request';
                      return (
                        <div key={n.id} style={{
                          padding: '10px 12px', borderBottom: '1px solid #f3f4f6', fontSize: '13px',
                          backgroundColor: isClientRequest ? '#fff7ed' : 'white',
                          borderLeft: isClientRequest ? '4px solid #ea580c' : 'none',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                            <div style={{ flex: 1 }}>
                              {isClientRequest && (
                                <span style={{
                                  display: 'inline-block', padding: '1px 6px', fontSize: '10px', fontWeight: '700',
                                  backgroundColor: '#ea580c', color: 'white', borderRadius: '4px', marginBottom: '4px',
                                }}>CLIENT REQUEST</span>
                              )}
                              <div style={{ fontWeight: '500', color: '#111827' }}>{n.title}</div>
                              {n.message && <div style={{ color: isClientRequest ? '#9a3412' : '#6b7280', fontSize: '12px', marginTop: '2px' }}>{n.message}</div>}
                              <div style={{ color: '#9ca3af', fontSize: '11px', marginTop: '2px' }}>
                                {n.created_at ? new Date(n.created_at.endsWith('Z') ? n.created_at : n.created_at + 'Z').toLocaleString() : ''}
                              </div>
                            </div>
                            {isClientRequest && (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const config = await getAuthConfig();
                                    await axios.post(`${API_URL}/jobs/${id}/portal/notifications/${n.id}/add-to-checklist`, {}, config);
                                    alert('Added to field checklist!');
                                    loadFieldChecklists();
                                  } catch (err) {
                                    alert(err.response?.data?.error || 'Failed to add to checklist');
                                  }
                                }}
                                style={{
                                  padding: '6px 10px', backgroundColor: '#dc2626', color: 'white',
                                  border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px',
                                  fontWeight: '600', whiteSpace: 'nowrap', flexShrink: 0,
                                  display: 'flex', alignItems: 'center', gap: '4px',
                                }}
                                title="Add this request to the field checklist as an important item"
                              >
                                <FiCheckSquare size={12} /> Add to Checklist
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Contract & Change Orders Section */}
        {canManagePayroll && (
          <div style={{ marginTop: '2rem', padding: '1.5rem', background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
              onClick={() => setShowChangeOrders(!showChangeOrders)}
            >
              <h3 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiFile size={20} /> Contract & Change Orders
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {contractAmount > 0 && (
                  <span style={{ fontSize: '14px', color: '#374151', fontWeight: 600 }}>
                    Contract: ${(coSummary.total_contract_value || contractAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                )}
                {changeOrders.length > 0 && (
                  <span style={{ fontSize: '12px', backgroundColor: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
                    {changeOrders.length} CO{changeOrders.length !== 1 ? 's' : ''}
                  </span>
                )}
                <FiChevronRight size={18} style={{ transform: showChangeOrders ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
              </div>
            </div>

            {showChangeOrders && (
              <div style={{ marginTop: '16px' }}>
                {/* Contract Amount */}
                <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <strong style={{ fontSize: '13px', color: '#6b7280' }}>Original Contract Amount</strong>
                    {canManagePayroll && !editingContract && (
                      <button onClick={() => setEditingContract(true)} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
                    )}
                  </div>
                  {editingContract ? (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span style={{ fontSize: '18px', fontWeight: 600 }}>$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={contractAmount}
                        onChange={e => setContractAmount(e.target.value)}
                        style={{ flex: 1, padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '16px' }}
                      />
                      <button onClick={handleSaveContract} style={{ padding: '8px 16px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>Save</button>
                      <button onClick={() => setEditingContract(false)} style={{ padding: '8px 12px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ fontSize: '22px', fontWeight: 700, color: '#111827' }}>
                      {contractAmount ? `$${parseFloat(contractAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : <span style={{ color: '#9ca3af', fontSize: '14px', fontWeight: 400 }}>Not set</span>}
                    </div>
                  )}
                </div>

                {/* Contract Summary (if change orders exist) */}
                {changeOrders.length > 0 && (
                  <div style={{ background: '#eff6ff', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #bfdbfe' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
                      <span>Original Contract:</span>
                      <span>${parseFloat(contractAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px', color: '#1e40af' }}>
                      <span>Change Orders ({changeOrders.filter(c => c.status !== 'Rejected').length}):</span>
                      <span>+${(coSummary.total_change_orders || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '15px', borderTop: '2px solid #1e40af', paddingTop: '8px', marginTop: '4px' }}>
                      <span>Total Contract Value:</span>
                      <span>${(coSummary.total_contract_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                )}

                {/* Change Orders List */}
                {changeOrders.map(co => (
                  <div key={co.id} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <strong style={{ fontSize: '14px' }}>{co.co_number}</strong>
                          <span style={{
                            fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px',
                            backgroundColor: co.status === 'Approved' ? '#dcfce7' : co.status === 'Submitted' ? '#fef3c7' : co.status === 'Rejected' ? '#fee2e2' : '#f3f4f6',
                            color: co.status === 'Approved' ? '#166534' : co.status === 'Submitted' ? '#92400e' : co.status === 'Rejected' ? '#991b1b' : '#374151'
                          }}>
                            {co.status}
                          </span>
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: 500, marginTop: '2px' }}>{co.title}</div>
                        {co.description && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{co.description.substring(0, 150)}{co.description.length > 150 ? '...' : ''}</div>}
                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                          Created: {new Date(co.date_created + 'T00:00:00').toLocaleDateString()}
                          {co.requested_by && ` • Requested by: ${co.requested_by}`}
                        </div>
                        {co.client_signature && (
                          <div style={{ fontSize: '12px', color: '#166534', marginTop: '2px', fontWeight: 500 }}>
                            ✓ Signed by {co.client_signature}
                            {co.client_signed_at && ` on ${new Date(co.client_signed_at + 'Z').toLocaleDateString()}`}
                          </div>
                        )}
                        {co.last_sent_at && !co.client_signature && (
                          <div style={{ fontSize: '12px', color: '#2563eb', marginTop: '2px' }}>
                            Sent to client {new Date(co.last_sent_at + 'Z').toLocaleDateString()} — awaiting response
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e40af' }}>
                          ${(co.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </div>
                        {canManagePayroll && (
                          <div style={{ display: 'flex', gap: '4px', marginTop: '8px', justifyContent: 'flex-end' }}>
                            {co.status === 'Draft' && (
                              <button onClick={() => handleCOStatusChange(co, 'Submitted')} style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '4px', cursor: 'pointer' }}>Submit</button>
                            )}
                            {co.status === 'Submitted' && (
                              <button onClick={() => handleCOStatusChange(co, 'Approved')} style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: '4px', cursor: 'pointer' }}>Approve</button>
                            )}
                            <button onClick={() => openCOPdf(co.id)} style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '4px', cursor: 'pointer' }}>
                              <FiPrinter size={11} /> PDF
                            </button>
                            {co.status !== 'Approved' && co.status !== 'Rejected' && (
                              <button onClick={() => handleEmailCO(co)} style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '4px', cursor: 'pointer' }} title="Email approval link to client">
                                <FiMail size={11} /> Email
                              </button>
                            )}
                            <button onClick={() => { setEditingCO(co); setCOForm({ title: co.title, description: co.description || '', reason: co.reason || '', amount: co.amount || '', requested_by: co.requested_by || '' }); setShowCOForm(true); }} style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>
                              <FiEdit size={11} />
                            </button>
                            <button onClick={() => handleDeleteCO(co.id)} style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '4px', cursor: 'pointer', color: '#991b1b' }}>
                              <FiTrash2 size={11} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add Change Order Button */}
                {canManagePayroll && (
                  <button
                    onClick={() => { setEditingCO(null); setCOForm({ title: '', description: '', reason: '', amount: '', requested_by: '' }); setShowCOForm(true); }}
                    style={{ width: '100%', padding: '10px', backgroundColor: '#eff6ff', color: '#1e40af', border: '2px dashed #93c5fd', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '14px', marginTop: '8px' }}
                  >
                    + Add Change Order
                  </button>
                )}

                {/* Change Order Form Modal */}
                {showCOForm && (
                  <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px', maxHeight: 'calc(90dvh - 90px)', overflowY: 'auto' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 style={{ margin: 0 }}>{editingCO ? `Edit ${editingCO.co_number}` : 'New Change Order'}</h3>
                        <button onClick={() => { setShowCOForm(false); setEditingCO(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={20} /></button>
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Title *</label>
                        <input value={coForm.title} onChange={e => setCOForm({ ...coForm, title: e.target.value })}
                          placeholder="e.g. Additional topo for grading plan"
                          style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Amount *</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ fontWeight: 600 }}>$</span>
                          <input type="number" step="0.01" value={coForm.amount} onChange={e => setCOForm({ ...coForm, amount: e.target.value })}
                            placeholder="0.00"
                            style={{ flex: 1, padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }} />
                        </div>
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Description of Additional Work</label>
                        <textarea value={coForm.description} onChange={e => setCOForm({ ...coForm, description: e.target.value })}
                          rows={3} placeholder="Describe the additional scope of work..."
                          style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical' }} />
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Reason for Change</label>
                        <textarea value={coForm.reason} onChange={e => setCOForm({ ...coForm, reason: e.target.value })}
                          rows={2} placeholder="Why is this change needed?"
                          style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical' }} />
                      </div>

                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Requested By</label>
                        <input value={coForm.requested_by} onChange={e => setCOForm({ ...coForm, requested_by: e.target.value })}
                          placeholder="e.g. Project Manager name"
                          style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={handleCOSubmit} disabled={!coForm.title || !coForm.amount}
                          style={{ flex: 1, padding: '12px', backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', opacity: (!coForm.title || !coForm.amount) ? 0.5 : 1 }}>
                          {editingCO ? 'Update Change Order' : 'Create Change Order'}
                        </button>
                        <button onClick={() => { setShowCOForm(false); setEditingCO(null); }}
                          style={{ padding: '12px 20px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Work Orders Section - All users can see, only payroll can edit */}
        {workOrders.length > 0 || canManagePayroll ? (
          <div className="work-orders-section" style={{
            marginTop: '2rem',
            padding: '1.5rem',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                cursor: 'pointer',
                marginBottom: showWorkOrders ? '1rem' : 0
              }}
              onClick={() => setShowWorkOrders(!showWorkOrders)}
            >
              <h2 style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                margin: 0,
                color: '#7c3aed'
              }}>
                📋 Work Orders
                {workOrders.length > 0 && (
                  <span style={{
                    fontSize: '14px',
                    backgroundColor: '#7c3aed',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    marginLeft: '8px'
                  }}>
                    {workOrders.length}
                  </span>
                )}
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {workOrders.length > 0 && (
                  <span style={{ fontSize: '14px', color: '#16a34a', fontWeight: '600' }}>
                    ${(workOrdersSummary.total_paid || 0).toLocaleString()} paid
                  </span>
                )}
                <span style={{ transform: showWorkOrders ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                  ▼
                </span>
              </div>
            </div>

            {showWorkOrders && (
              <>
                {/* Summary - Only for payroll managers */}
                {canManagePayroll && workOrders.length > 0 && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '10px',
                    marginBottom: '15px',
                    padding: '12px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#666' }}>Quoted</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                        ${(workOrdersSummary.total_quoted || 0).toLocaleString()}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#666' }}>Invoiced</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#3b82f6' }}>
                        ${(workOrdersSummary.total_invoiced || 0).toLocaleString()}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#666' }}>Paid</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#16a34a' }}>
                        ${(workOrdersSummary.total_paid || 0).toLocaleString()}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#666' }}>Pending Invoice</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#f59e0b' }}>
                        ${(workOrdersSummary.pending_invoice || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Work Orders List */}
                {workOrders.length > 0 ? (
                  <div style={{ marginBottom: '15px' }}>
                    {workOrders.map(wo => (
                      <div key={wo.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px',
                        borderBottom: '1px solid #eee',
                        gap: '10px'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <strong style={{ color: '#7c3aed' }}>{wo.order_number}</strong>
                            <span style={{
                              fontSize: '11px',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              backgroundColor: getWorkOrderStatusColor(wo.status) + '20',
                              color: getWorkOrderStatusColor(wo.status)
                            }}>
                              {wo.status}
                            </span>
                            {wo.po_number && (
                              <span style={{ fontSize: '12px', color: '#666' }}>
                                PO: {wo.po_number}
                              </span>
                            )}
                            {wo.included_in_contract && (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: '#0369a1', background: '#e0f2fe', padding: '2px 8px', borderRadius: '12px' }}>IN CONTRACT</span>
                            )}
                          </div>
                          <div style={{ fontSize: '14px', color: '#333' }}>{wo.description}</div>
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            {wo.scheduled_date && `Scheduled: ${wo.scheduled_date}`}
                            {wo.total_hours > 0 && ` • ${wo.total_hours}h logged`}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', minWidth: '100px' }}>
                          {canManagePayroll && (
                            <>
                              <div style={{ fontWeight: '600', color: '#333' }}>
                                {wo.included_in_contract ? <span style={{ color: '#64748b', fontSize: '13px', fontWeight: 500 }}>Included</span> : `$${(wo.quoted_amount || 0).toLocaleString()}`}
                              </div>
                              {wo.invoiced_amount > 0 && (
                                <div style={{ fontSize: '12px', color: '#3b82f6' }}>
                                  Inv: ${wo.invoiced_amount.toLocaleString()}
                                </div>
                              )}
                              {wo.paid_amount > 0 && (
                                <div style={{ fontSize: '12px', color: '#16a34a' }}>
                                  Paid: ${wo.paid_amount.toLocaleString()}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        {canManagePayroll && (
                          <div style={{ display: 'flex', gap: '5px' }}>
                            <button
                              onClick={() => handleEditWorkOrder(wo)}
                              style={{
                                padding: '6px 10px',
                                border: 'none',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteWorkOrder(wo.id)}
                              style={{
                                padding: '6px 10px',
                                border: 'none',
                                backgroundColor: '#fee2e2',
                                color: '#dc2626',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              <FiTrash2 size={12} />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '15px' }}>
                    {canManagePayroll 
                      ? 'No work orders yet. Add work orders for multi-phase projects with separate PO numbers.'
                      : 'No work orders for this job.'}
                  </p>
                )}

                {/* Add Work Order Button - Only for payroll managers */}
                {canManagePayroll && (
                  <button
                    onClick={() => {
                      setEditingWorkOrder(null);
                      setWorkOrderForm({
                        description: '',
                        po_number: '',
                        quoted_amount: '',
                        status: 'Pending',
                        scheduled_date: '',
                        notes: ''
                      });
                      setShowWorkOrderForm(true);
                    }}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#7c3aed',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    + Add Work Order
                  </button>
                )}

                {/* Work Order Form Modal */}
                {showWorkOrderForm && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                  }}>
                    <div style={{
                      backgroundColor: 'white',
                      padding: '24px',
                      borderRadius: '12px',
                      width: '90%',
                      maxWidth: '500px',
                      maxHeight: 'calc(90dvh - 90px)',
                      overflowY: 'auto'
                    }}>
                      <h3 style={{ marginTop: 0 }}>
                        {editingWorkOrder ? 'Edit Work Order' : 'New Work Order'}
                      </h3>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                          Description *
                        </label>
                        <input
                          type="text"
                          value={workOrderForm.description}
                          onChange={(e) => setWorkOrderForm({ ...workOrderForm, description: e.target.value })}
                          placeholder="e.g., Initial Boundary Survey"
                          style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: '6px',
                            border: '1px solid #ddd'
                          }}
                        />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            PO Number
                          </label>
                          <input
                            type="text"
                            value={workOrderForm.po_number}
                            onChange={(e) => setWorkOrderForm({ ...workOrderForm, po_number: e.target.value })}
                            placeholder="Client's PO #"
                            style={{
                              width: '100%',
                              padding: '10px',
                              borderRadius: '6px',
                              border: '1px solid #ddd'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Quoted Amount
                          </label>
                          <input
                            type="number"
                            value={workOrderForm.quoted_amount}
                            onChange={(e) => setWorkOrderForm({ ...workOrderForm, quoted_amount: e.target.value })}
                            placeholder="0.00"
                            style={{
                              width: '100%',
                              padding: '10px',
                              borderRadius: '6px',
                              border: '1px solid #ddd'
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Status
                          </label>
                          <select
                            value={workOrderForm.status}
                            onChange={(e) => setWorkOrderForm({ ...workOrderForm, status: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '10px',
                              borderRadius: '6px',
                              border: '1px solid #ddd'
                            }}
                          >
                            <option value="Pending">Pending</option>
                            <option value="Scheduled">Scheduled</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Completed">Completed</option>
                            <option value="Invoiced">Invoiced</option>
                            <option value="Paid">Paid</option>
                            <option value="On Hold">On Hold</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Scheduled Date
                          </label>
                          <input
                            type="date"
                            value={workOrderForm.scheduled_date}
                            onChange={(e) => setWorkOrderForm({ ...workOrderForm, scheduled_date: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '10px',
                              borderRadius: '6px',
                              border: '1px solid #ddd'
                            }}
                          />
                        </div>
                      </div>

                      {editingWorkOrder && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                              Invoiced Amount
                            </label>
                            <input
                              type="number"
                              value={workOrderForm.invoiced_amount || ''}
                              onChange={(e) => setWorkOrderForm({ ...workOrderForm, invoiced_amount: e.target.value })}
                              placeholder="0.00"
                              style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '6px',
                                border: '1px solid #ddd'
                              }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                              Paid Amount
                            </label>
                            <input
                              type="number"
                              value={workOrderForm.paid_amount || ''}
                              onChange={(e) => setWorkOrderForm({ ...workOrderForm, paid_amount: e.target.value })}
                              placeholder="0.00"
                              style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '6px',
                                border: '1px solid #ddd'
                              }}
                            />
                          </div>
                        </div>
                      )}

                      <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                          Notes
                        </label>
                        <textarea
                          value={workOrderForm.notes}
                          onChange={(e) => setWorkOrderForm({ ...workOrderForm, notes: e.target.value })}
                          placeholder="Internal notes..."
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: '6px',
                            border: '1px solid #ddd',
                            resize: 'vertical'
                          }}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => {
                            setShowWorkOrderForm(false);
                            setEditingWorkOrder(null);
                          }}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#e5e7eb',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleWorkOrderSubmit}
                          disabled={!workOrderForm.description}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#7c3aed',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            opacity: workOrderForm.description ? 1 : 0.5
                          }}
                        >
                          {editingWorkOrder ? 'Update' : 'Create'} Work Order
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : null}

        {/* Property Boundary Section */}
        <div className="boundary-section" style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <FiMap /> Property Boundary
          </h2>
          
          {job.boundary_file ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              backgroundColor: '#e8f5e9',
              borderRadius: '8px',
              border: '1px solid #c8e6c9',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FiMap style={{ color: '#388e3c' }} />
                <span style={{ color: '#2e7d32', fontWeight: '500' }}>
                  Boundary file uploaded
                </span>
                <span style={{ color: '#666', fontSize: '13px' }}>
                  ({job.boundary_file.split('/').pop().split('\\').pop()})
                </span>
              </div>
              <button
                onClick={handleBoundaryDelete}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#ffebee',
                  color: '#c62828',
                  border: '1px solid #ffcdd2',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '13px'
                }}
              >
                <FiTrash2 size={14} /> Remove
              </button>
            </div>
          ) : (
            <div style={{
              border: '2px dashed #ddd',
              borderRadius: '8px',
              padding: '30px',
              textAlign: 'center',
              backgroundColor: '#fafafa'
            }}>
              <FiUpload size={32} style={{ color: '#999', marginBottom: '10px' }} />
              <p style={{ color: '#666', marginBottom: '15px' }}>
                Upload a KML, KMZ, or GeoJSON file to show property boundaries on the map
              </p>
              <label style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                backgroundColor: '#1976d2',
                color: 'white',
                borderRadius: '6px',
                cursor: uploadingBoundary ? 'not-allowed' : 'pointer',
                opacity: uploadingBoundary ? 0.7 : 1
              }}>
                <FiUpload />
                {uploadingBoundary ? 'Uploading...' : 'Choose File'}
                <input
                  type="file"
                  accept=".kml,.kmz,.geojson"
                  onChange={handleBoundaryUpload}
                  disabled={uploadingBoundary}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          )}
        </div>

        {/* Job Photos Section */}
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <FiCamera /> Job Photos
            </h2>
            <label style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              borderRadius: '6px',
              cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
              opacity: uploadingPhoto ? 0.7 : 1,
              fontSize: '14px'
            }}>
              <FiCamera />
              {uploadingPhoto ? 'Uploading...' : 'Add Photo'}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoUpload}
                disabled={uploadingPhoto}
                style={{ display: 'none' }}
              />
            </label>
            {canManagePayroll && photos.length > 0 && (
              <button
                onClick={() => setShowPhotoEmail(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', marginLeft: '8px' }}
              >
                <FiMail /> Email to Client
              </button>
            )}
          </div>

          {showPhotoEmail && (
            <EmailToClientModal
              jobId={job.id}
              items={photos.map(p => ({ id: p.id, kind: 'photo', name: p.original_filename || `photo-${p.id}`, mime: 'image/jpeg', thumbUrl: `${API_BASE_URL || ''}${p.url}`, size: p.file_size, caption: p.caption }))}
              onClose={() => setShowPhotoEmail(false)}
            />
          )}

          {photos.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '12px'
            }}>
              {photos.map(photo => (
                <div
                  key={photo.id}
                  onClick={() => setSelectedPhoto(photo)}
                  style={{
                    position: 'relative',
                    paddingBottom: '100%',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <img
                    src={`${API_BASE_URL || ''}${photo.url}`}
                    alt={photo.original_filename || 'Job photo'}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              border: '2px dashed #ddd',
              borderRadius: '8px',
              padding: '40px',
              textAlign: 'center',
              backgroundColor: '#fafafa'
            }}>
              <FiImage size={40} style={{ color: '#ccc', marginBottom: '10px' }} />
              <p style={{ color: '#999', margin: 0 }}>No photos yet. Add photos from the field!</p>
            </div>
          )}
        </div>

        {/* Imported Parcel Data Section */}
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <FiDatabase /> Parcel Data
            </h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              {jobParcels.length > 0 && (
                <button
                  onClick={handleClearAllParcels}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  <FiTrash2 /> Clear All
                </button>
              )}
              <button
                onClick={openParcelImport}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  backgroundColor: '#6366f1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                <FiFolder /> Import from Scraper
              </button>
            </div>
          </div>

          {jobParcels.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Parcel ID</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Owner</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Acres</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Land Lot</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Deeds</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Plats</th>
                    <th style={{ padding: '10px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobParcels.map((parcel) => (
                    <tr key={parcel.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '10px', fontWeight: '500' }}>{parcel.parcel_id}</td>
                      <td style={{ padding: '10px' }}>
                        <div>{parcel.owner}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{parcel.owner_address}</div>
                      </td>
                      <td style={{ padding: '10px' }}>{parcel.acres}</td>
                      <td style={{ padding: '10px' }}>{parcel.land_lot} / {parcel.district}</td>
                      <td style={{ padding: '10px' }}>
                        {parcel.deeds && parcel.deeds.length > 0 ? (
                          <span style={{ color: '#059669' }}>{parcel.deed_count} deed(s)</span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '10px' }}>
                        {parcel.plats && parcel.plats.length > 0 ? (
                          <span style={{ color: '#2563eb' }}>{parcel.plat_count} plat(s)</span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleDeleteParcel(parcel.id)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{
              border: '2px dashed #ddd',
              borderRadius: '8px',
              padding: '40px',
              textAlign: 'center',
              backgroundColor: '#fafafa'
            }}>
              <FiDatabase size={40} style={{ color: '#ccc', marginBottom: '10px' }} />
              <p style={{ color: '#999', margin: 0 }}>No parcel data imported yet.</p>
              <p style={{ color: '#666', margin: '8px 0 0', fontSize: '14px' }}>
                Use the Parcel Scraper to look up property data, then import it here.
              </p>
            </div>
          )}
        </div>

        {/* Attachments — plans, PDFs, photos crews need on-site */}
        <div style={{ marginTop: '2rem' }}>
          <JobAttachments jobId={job.id} />
        </div>

        {/* Job Comments Section */}
        <div style={{ marginTop: '2rem' }}>
          <JobComments job={job} onJobUpdated={loadJob} />
        </div>

        {/* Letters referencing this job */}
        <RelatedLetters relatedType="job" relatedId={job.id} />

        {/* Job Profitability Section (Admin Only) */}
        {canManagePayroll && (
          <div style={{
            marginTop: '2rem',
            padding: '1.5rem',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div 
              onClick={() => setShowProfitability(!showProfitability)}
              style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                cursor: 'pointer',
                marginBottom: showProfitability ? '1.5rem' : 0
              }}
            >
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0, color: '#16a34a' }}>
                💰 Job Profitability
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {profitability && (
                  <span style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: profitability.profit >= 0 ? '#16a34a' : '#dc2626'
                  }}>
                    {profitability.profit >= 0 ? '+' : ''}${profitability.profit.toLocaleString()} ({profitability.margin_percent}%)
                  </span>
                )}
                <span style={{ color: '#666', fontSize: '20px' }}>{showProfitability ? '▼' : '▶'}</span>
              </div>
            </div>

            {showProfitability && profitability && (
              <>
                {/* Profit Summary */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '15px',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#f0fdf4',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#16a34a' }}>
                      ${profitability.revenue.toLocaleString()}
                    </div>
                    <div style={{ color: '#666', fontSize: '13px' }}>Revenue</div>
                  </div>
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#fef2f2',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626' }}>
                      ${profitability.total_costs.toLocaleString()}
                    </div>
                    <div style={{ color: '#666', fontSize: '13px' }}>Total Costs</div>
                  </div>
                  <div style={{
                    padding: '15px',
                    backgroundColor: profitability.profit >= 0 ? '#f0fdf4' : '#fef2f2',
                    borderRadius: '8px',
                    textAlign: 'center',
                    border: '2px solid ' + (profitability.profit >= 0 ? '#16a34a' : '#dc2626')
                  }}>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: 'bold', 
                      color: profitability.profit >= 0 ? '#16a34a' : '#dc2626' 
                    }}>
                      ${profitability.profit.toLocaleString()}
                    </div>
                    <div style={{ color: '#666', fontSize: '13px' }}>Profit ({profitability.margin_percent}%)</div>
                  </div>
                </div>

                {/* Cost Breakdown */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '16px', marginBottom: '10px', color: '#374151' }}>Cost Breakdown</h3>
                  <div style={{ 
                    backgroundColor: '#f9fafb', 
                    borderRadius: '8px', 
                    padding: '15px',
                    display: 'grid',
                    gap: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #e5e7eb' }}>
                      <span>Labor ({profitability.costs.labor_hours} hrs)</span>
                      <span style={{ fontWeight: '500' }}>${profitability.costs.labor.toLocaleString()}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #e5e7eb' }}>
                      <span>Mileage ({profitability.costs.distance_miles.toFixed(1)} mi round trip{profitability.costs.distance_source === 'straight_line' ? ', straight-line estimate' : ''})</span>
                      <span style={{ fontWeight: '500' }}>${profitability.costs.mileage.toLocaleString()}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #e5e7eb' }}>
                      <span>Materials ({profitability.costs.rebar_count} rebar, {profitability.costs.pipe_count} pipe)</span>
                      <span style={{ fontWeight: '500' }}>${profitability.costs.materials.toLocaleString()}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #e5e7eb' }}>
                      <span>Overhead</span>
                      <span style={{ fontWeight: '500' }}>${profitability.costs.overhead.toLocaleString()}</span>
                    </div>
                    {profitability.costs.other > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #e5e7eb' }}>
                        <span>Other {profitability.costs.other_note && `(${profitability.costs.other_note})`}</span>
                        <span style={{ fontWeight: '500' }}>${profitability.costs.other.toLocaleString()}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontWeight: 'bold', fontSize: '15px' }}>
                      <span>Total Costs</span>
                      <span>${profitability.total_costs.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Edit Expenses */}
                <div style={{ 
                  borderTop: '1px solid #e5e7eb', 
                  paddingTop: '1.5rem' 
                }}>
                  <h3 style={{ fontSize: '16px', marginBottom: '15px', color: '#374151' }}>Edit Job Expenses</h3>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                    gap: '15px',
                    marginBottom: '15px'
                  }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', color: '#666' }}>Rebar Used</label>
                      <input
                        type="number"
                        value={expenses.rebar_count}
                        onChange={(e) => setExpenses({...expenses, rebar_count: parseInt(e.target.value) || 0})}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', color: '#666' }}>Pipe/Caps Used</label>
                      <input
                        type="number"
                        value={expenses.pipe_count}
                        onChange={(e) => setExpenses({...expenses, pipe_count: parseInt(e.target.value) || 0})}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', color: '#666' }}>Number of Trips</label>
                      <input
                        type="number"
                        value={expenses.num_trips}
                        onChange={(e) => setExpenses({...expenses, num_trips: parseInt(e.target.value) || 1})}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', color: '#666' }}>Crew Vehicles</label>
                      <input
                        type="number"
                        value={expenses.crew_size}
                        onChange={(e) => setExpenses({...expenses, crew_size: parseInt(e.target.value) || 1})}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', color: '#666' }}>Other Expenses ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={expenses.other_expenses}
                        onChange={(e) => setExpenses({...expenses, other_expenses: parseFloat(e.target.value) || 0})}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', color: '#666' }}>Other Note</label>
                      <input
                        type="text"
                        value={expenses.other_expenses_note || ''}
                        onChange={(e) => setExpenses({...expenses, other_expenses_note: e.target.value})}
                        placeholder="e.g., Equipment rental"
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                  </div>
                  <button
                    onClick={saveExpenses}
                    disabled={savingExpenses}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#16a34a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    {savingExpenses ? 'Saving...' : 'Update Expenses & Recalculate'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Photo Viewer Modal */}
        {selectedPhoto && (
          <div
            onClick={() => setSelectedPhoto(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              // Above the mobile bottom nav (zIndex 99980) so it actually covers the screen
              zIndex: 999999,
              padding: isMobile ? '12px' : '20px',
              paddingTop: isMobile ? 'max(12px, env(safe-area-inset-top))' : '20px',
              paddingBottom: isMobile ? 'max(12px, env(safe-area-inset-bottom))' : '20px',
            }}
          >
            {/* Big X close button — top-left so the thumb naturally reaches it on phones */}
            <button
              onClick={() => setSelectedPhoto(null)}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: isMobile ? 'max(12px, env(safe-area-inset-top))' : '20px',
                left: isMobile ? '12px' : '20px',
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.15)',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                lineHeight: 1,
              }}
            >
              <FiX size={24} />
            </button>

            {/* Action buttons — top right; icon-only on mobile to save space */}
            <div style={{
              position: 'absolute',
              top: isMobile ? 'max(12px, env(safe-area-inset-top))' : '20px',
              right: isMobile ? '12px' : '20px',
              display: 'flex',
              gap: '8px',
            }}>
              <button
                onClick={(e) => { e.stopPropagation(); handlePhotoDownload(selectedPhoto); }}
                aria-label="Download"
                style={{
                  padding: isMobile ? '0' : '10px 20px',
                  width: isMobile ? '44px' : 'auto',
                  height: isMobile ? '44px' : 'auto',
                  borderRadius: isMobile ? '50%' : '6px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <FiDownload size={isMobile ? 20 : 16} /> {!isMobile && 'Download'}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handlePhotoDelete(selectedPhoto.id); }}
                aria-label="Delete"
                style={{
                  padding: isMobile ? '0' : '10px 20px',
                  width: isMobile ? '44px' : 'auto',
                  height: isMobile ? '44px' : 'auto',
                  borderRadius: isMobile ? '50%' : '6px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <FiTrash2 size={isMobile ? 20 : 16} /> {!isMobile && 'Delete'}
              </button>
            </div>

            <img
              src={`${API_BASE_URL || ''}${selectedPhoto.url}`}
              alt={selectedPhoto.original_filename || 'Job photo'}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: isMobile ? '100%' : '90%',
                maxHeight: isMobile ? 'calc(100dvh - 160px)' : '80vh',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
            <div style={{ color: 'white', marginTop: '12px', textAlign: 'center', fontSize: '13px', opacity: 0.85 }}>
              <p style={{ margin: '5px 0' }}>
                Uploaded by {selectedPhoto.uploaded_by_name} on {parseUTCDate(selectedPhoto.created_at)?.toLocaleString()}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Map Picker Modal */}
      {showMapPicker && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '900px',
            height: '80vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '15px 20px',
              borderBottom: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0 }}>Pick Location on Map</h3>
              <button
                type="button"
                onClick={cancelMapSelection}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: '15px 20px', borderBottom: '1px solid #eee', display: 'flex', gap: '10px' }}>
              <input
                type="text"
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), searchAddressOnMap())}
                placeholder="Search address (e.g., 123 Main St, Atlanta, GA)"
                style={{ flex: 1, padding: '10px 15px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
              />
              <button
                type="button"
                onClick={searchAddressOnMap}
                disabled={searching}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px',
                  backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '6px',
                  cursor: searching ? 'not-allowed' : 'pointer', opacity: searching ? 0.7 : 1
                }}
              >
                <FiSearch />
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>

            <div style={{ flex: 1, position: 'relative' }}>
              <MapContainer center={mapCenter} zoom={12} style={{ height: '100%', width: '100%' }}>
                {mapView === 'aerial' ? (
                  <TileLayer
                    attribution='&copy; Esri'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  />
                ) : (
                  <TileLayer
                    attribution='&copy; OpenStreetMap'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                )}
                <MapClickHandler onMapClick={handleMapClick} />
                <MapRecenter center={mapCenter} />
                {tempCoords.lat && tempCoords.lng && (
                  <Marker position={[tempCoords.lat, tempCoords.lng]} />
                )}
              </MapContainer>
              
              <div style={{
                position: 'absolute', top: '10px', right: '10px', zIndex: 1000,
                display: 'flex', borderRadius: '6px', overflow: 'hidden', boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
              }}>
                <button type="button" onClick={() => setMapView('street')}
                  style={{ padding: '8px 14px', backgroundColor: mapView === 'street' ? '#1976d2' : 'white',
                    color: mapView === 'street' ? 'white' : '#333', border: 'none', cursor: 'pointer', fontSize: '13px' }}>
                  Street
                </button>
                <button type="button" onClick={() => setMapView('aerial')}
                  style={{ padding: '8px 14px', backgroundColor: mapView === 'aerial' ? '#1976d2' : 'white',
                    color: mapView === 'aerial' ? 'white' : '#333', border: 'none', borderLeft: '1px solid #ddd', cursor: 'pointer', fontSize: '13px' }}>
                  Aerial
                </button>
              </div>
              
              <div style={{
                position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.7)', color: 'white', padding: '8px 16px',
                borderRadius: '20px', fontSize: '13px', zIndex: 1000, pointerEvents: 'none'
              }}>
                Click on the map to set location
              </div>
            </div>

            <div style={{
              padding: '15px 20px', borderTop: '1px solid #ddd',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div style={{ fontSize: '14px', color: '#666' }}>
                {tempCoords.lat && tempCoords.lng ? (
                  <span>Selected: <strong>{tempCoords.lat.toFixed(6)}, {tempCoords.lng.toFixed(6)}</strong></span>
                ) : (
                  <span>No location selected</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={cancelMapSelection}
                  style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="button" onClick={confirmMapSelection} disabled={!tempCoords.lat || !tempCoords.lng}
                  style={{ padding: '10px 20px', backgroundColor: tempCoords.lat ? '#28a745' : '#ccc',
                    color: 'white', border: 'none', borderRadius: '6px', cursor: tempCoords.lat ? 'pointer' : 'not-allowed' }}>
                  Confirm Location
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .team-section {
          margin-top: 2rem;
          padding: 1.5rem;
          background: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .team-section h2 {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
          color: #333;
        }

        .assigned-team-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .team-member-card {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          background: #f5f5f5;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
        }

        .member-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 1.1rem;
        }

        .member-info {
          flex: 1;
        }

        .member-name {
          font-weight: 600;
          color: #333;
          margin-bottom: 0.25rem;
        }

        .member-role {
          font-size: 0.9rem;
          color: #666;
        }

        .no-team-members {
          color: #999;
          font-style: italic;
          padding: 1rem;
          text-align: center;
        }

        .self-assign-section {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e0e0e0;
        }
      `}</style>

      {/* Parcel Import Modal */}
      {showParcelImport && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '900px',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '15px 20px',
              borderBottom: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0 }}>
                <FiFolder style={{ marginRight: '8px' }} />
                Import Parcel Data from Scraper
              </h3>
              <button
                onClick={() => setShowParcelImport(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}
              >
                &times;
              </button>
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
              {/* JSON Upload Section */}
              <div style={{
                padding: '16px',
                marginBottom: '20px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bae6fd'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '8px', color: '#0369a1' }}>
                  <FiFile style={{ marginRight: '6px' }} />
                  Upload JSON Export
                </div>
                <p style={{ fontSize: '13px', color: '#666', margin: '0 0 10px' }}>
                  Upload a <code>parcels_export.json</code> file from the parcel scraper. This works on any server.
                </p>
                <label style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  borderRadius: '6px',
                  cursor: importingParcels ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: importingParcels ? 0.6 : 1
                }}>
                  <FiDatabase size={14} />
                  {importingParcels ? 'Importing...' : 'Choose JSON File'}
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleJsonUpload}
                    disabled={importingParcels}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>

              {/* Network Drive Section */}
              {loadingParcels ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <p>Loading...</p>
                </div>
              ) : !selectedCounty ? (
                <>
                  {parcelFolders.length > 0 ? (
                    <p style={{ marginBottom: '15px', color: '#666' }}>
                      Or select a county folder from your network drive (Z:\PropertyRecords):
                    </p>
                  ) : (
                    <p style={{ marginBottom: '15px', color: '#999', fontSize: '13px' }}>
                      Network drive (Z:) not available. Use JSON upload above, or connect the drive for folder browsing.
                    </p>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                    {parcelFolders.map((folder) => (
                      <button
                        key={folder.path}
                        onClick={() => selectCounty(folder)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '15px',
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                      >
                        <FiFolder size={24} style={{ color: '#f59e0b' }} />
                        <div>
                          <div style={{ fontWeight: '500' }}>{folder.name}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>{folder.project_count} project(s)</div>
                        </div>
                        <FiChevronRight style={{ marginLeft: 'auto', color: '#999' }} />
                      </button>
                    ))}
                  </div>
                  {parcelFolders.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      <p>No folders found. Make sure the network drive (Z:) is connected.</p>
                    </div>
                  )}
                </>
              ) : !selectedProject ? (
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <button
                      onClick={() => { setSelectedCounty(null); setParcelProjects([]); }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 12px',
                        backgroundColor: '#f1f5f9',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        marginBottom: '10px'
                      }}
                    >
                      ← Back to Counties
                    </button>
                    <p style={{ color: '#666', margin: 0 }}>
                      Select a project from <strong>{selectedCounty.name}</strong>:
                    </p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {parcelProjects.map((project) => (
                      <button
                        key={project.path}
                        onClick={() => selectProject(project)}
                        disabled={!project.has_data}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '12px 15px',
                          backgroundColor: project.has_data ? '#f8fafc' : '#f1f1f1',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          cursor: project.has_data ? 'pointer' : 'not-allowed',
                          textAlign: 'left',
                          opacity: project.has_data ? 1 : 0.6
                        }}
                      >
                        <FiFile size={20} style={{ color: project.has_data ? '#6366f1' : '#999' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500' }}>{project.name}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {project.csv_count} CSV, {project.kml_count} KML
                            {project.modified && ` • ${new Date(project.modified).toLocaleDateString()}`}
                          </div>
                        </div>
                        {project.has_data && <FiChevronRight style={{ color: '#999' }} />}
                      </button>
                    ))}
                  </div>
                  {parcelProjects.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      <p>No projects found in this county folder.</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <button
                      onClick={() => { setSelectedProject(null); setPreviewParcels([]); }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 12px',
                        backgroundColor: '#f1f5f9',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        marginBottom: '10px'
                      }}
                    >
                      ← Back to Projects
                    </button>
                    <p style={{ color: '#666', margin: 0 }}>
                      Preview parcels from <strong>{selectedProject.name}</strong>:
                    </p>
                  </div>

                  {previewParcels.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Parcel ID</th>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Owner</th>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Acres</th>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Deeds</th>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Plats</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewParcels.map((parcel, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '8px', fontWeight: '500' }}>{parcel.parcel_id}</td>
                              <td style={{ padding: '8px' }}>{parcel.owner}</td>
                              <td style={{ padding: '8px' }}>{parcel.acres}</td>
                              <td style={{ padding: '8px' }}>{parcel.deed_count || 0}</td>
                              <td style={{ padding: '8px' }}>{parcel.plat_count || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      <p>No parcel data found in this project.</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {selectedProject && previewParcels.length > 0 && (
              <div style={{
                padding: '15px 20px',
                borderTop: '1px solid #ddd',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#f9fafb'
              }}>
                <span style={{ color: '#666' }}>
                  {previewParcels.length} parcel(s) will be imported
                </span>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => setShowParcelImport(false)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleImportParcels}
                    disabled={importingParcels}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#16a34a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: importingParcels ? 'not-allowed' : 'pointer',
                      opacity: importingParcels ? 0.7 : 1
                    }}
                  >
                    {importingParcels ? 'Importing...' : 'Import Parcels'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Work Order Print Modal */}
      {showWorkOrder && (
        <WorkOrderPrint
          job={job}
          timeEntries={jobTimeEntries}
          onClose={() => setShowWorkOrder(false)}
        />
      )}
      {/* Field Checklist Edit Modal */}
      {editingFieldChecklist && (
        <FieldChecklistModal
          jobId={editingFieldChecklist.job_id}
          jobNumber={editingFieldChecklist.job_number}
          checklistId={editingFieldChecklist.id}
          mode="edit"
          onComplete={() => { setEditingFieldChecklist(null); loadFieldChecklists(); }}
          onClose={() => { setEditingFieldChecklist(null); loadFieldChecklists(); }}
        />
      )}

      {/* Notify Contacts Modal */}
      {showNotifyModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px',
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '500px', width: '100%', maxHeight: '80vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}><FiSend /> Notify Contacts</h3>
              <button onClick={() => setShowNotifyModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={20} /></button>
            </div>

            {/* Contact checkboxes */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '8px' }}>
                Select who to notify:
              </label>
              {jobContacts.map((c, i) => (
                <label key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px',
                  backgroundColor: notifySelection.includes(i) ? '#eff6ff' : '#f9fafb',
                  border: `1px solid ${notifySelection.includes(i) ? '#bfdbfe' : '#e5e7eb'}`,
                  borderRadius: '6px', marginBottom: '4px', cursor: 'pointer', fontSize: '13px',
                }}>
                  <input
                    type="checkbox"
                    checked={notifySelection.includes(i)}
                    onChange={() => {
                      setNotifySelection(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
                    }}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <div>
                    <strong>{c.name}</strong>
                    {c.role && <span style={{ color: '#6b7280' }}> — {c.role}</span>}
                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                      {c.phone && `SMS: ${c.phone}`}{c.phone && c.email && ' · '}{c.email && `Email: ${c.email}`}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            {/* Message */}
            <div style={{ marginBottom: '14px' }}>
              <input type="text" value={notifyMessage.title}
                onChange={(e) => setNotifyMessage(p => ({ ...p, title: e.target.value }))}
                placeholder="Title (e.g., Schedule Update, Field Work Complete)"
                style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', marginBottom: '6px', boxSizing: 'border-box' }}
              />
              <textarea value={notifyMessage.message}
                onChange={(e) => setNotifyMessage(p => ({ ...p, message: e.target.value }))}
                placeholder="Message details..."
                rows={3}
                style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical' }}
              />
            </div>

            {/* Send via checkboxes */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={notifyMessage.send_sms}
                  onChange={(e) => setNotifyMessage(p => ({ ...p, send_sms: e.target.checked }))} />
                Send SMS
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={notifyMessage.send_email}
                  onChange={(e) => setNotifyMessage(p => ({ ...p, send_email: e.target.checked }))} />
                Send Email
              </label>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowNotifyModal(false)} style={{
                padding: '10px 18px', backgroundColor: '#f5f5f5', border: '1px solid #ddd',
                borderRadius: '8px', cursor: 'pointer', fontSize: '14px',
              }}>Cancel</button>
              <button
                onClick={async () => {
                  if (!notifyMessage.title.trim()) { alert('Title is required'); return; }
                  if (notifySelection.length === 0) { alert('Select at least one contact'); return; }
                  try {
                    const config = await getAuthConfig();
                    const res = await axios.post(`${API_URL}/jobs/${id}/portal/notify-contacts`, {
                      contact_indices: notifySelection,
                      ...notifyMessage,
                    }, config);
                    alert(res.data.message);
                    setShowNotifyModal(false);
                  } catch (err) {
                    alert(err.response?.data?.error || 'Failed to notify');
                  }
                }}
                disabled={notifySelection.length === 0 || !notifyMessage.title.trim()}
                style={{
                  padding: '10px 18px',
                  backgroundColor: (notifySelection.length > 0 && notifyMessage.title.trim()) ? '#7c3aed' : '#9ca3af',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: (notifySelection.length > 0 && notifyMessage.title.trim()) ? 'pointer' : 'not-allowed',
                  fontSize: '14px', fontWeight: '600',
                }}>
                Send to {notifySelection.length} Contact{notifySelection.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default JobDetail;